// src/encounter/structured-note/structured-note-section.tsx
//
// PROD-2743 · frame v2 ONLY (`?ef=frameV2`; OFF never renders this file). The approved R6c note
// blocks (signal-ds #434, frozen by decision D-48) mounted inside the encounter chair's
// S · O · A&P · Wrap-up host (#572). Samer, 1 Oct: "The encounter template is a mess. It is a
// hybrid of old and new." This replaces the host's plain block wrappers with the DS NoteSection +
// NoteBlock, and draws HPI, ROS and PE with the structured blocks instead of the old renderer
// matrix. CONTRACT-template-to-encounter-v1 §1b is the mapping; note-adapter.ts implements it.
//
// WHAT IT DOES NOT MOUNT: `EncounterNoteComposer`. Its own section index and Sign would duplicate
// the host's ribbon and Wrap-up, and its Sign calls a composition endpoint that does not exist
// (§1b). The host's nav, autosave, sign and wrap-up are untouched.
//
// ONE LIVE RESPONSE. No block keeps an answer: each is handed a projection of `response` on every
// render, and every change is written through `write` onto the section's shared response ref and
// `onResponseChange` — the same path the renderer's own merge uses (encounter-shell-view.tsx
// StepCanvas). Steps this file does not redraw (Risk, Doc, A&P, the allied-health Plan, Wrap-up)
// render the host's existing step canvas inside their NoteBlock, unchanged.
//
// THIN ON THE DS. The blocks are under Samer's design review (1 Oct 05:30): this file passes PROPS
// only — no class overrides on a DS block — so a revised block drops in without touching it.
import { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState, type MutableRefObject, type ReactNode } from 'react';
import {
  Banner,
  formatPickerDate,
  BlankChip,
  Button,
  MacroMenu,
  NoteBlock,
  NOTE_CONTAINER,
  NoteLine,
  NoteSection,
  PickListChip,
  QuestionnaireRenderer,
  ReviewOfSystemsBlock,
  SectionEyebrow,
  SymptomChip,
  TextField,
  type AuthorRole,
  type ChangeOrigin,
  type CommandMenuItem,
  type NoteFieldCommands,
  type NoteMacro as DsNoteMacro,
  type NoteSectionKey,
  type QItem,
  type Questionnaire,
  type QuestionnaireRendererProps,
  type QuestionnaireResponse,
  type RosFindings,
  type RosSystem,
} from '@scripsteam/scrips-signal-ds';
import { noteBlockLabel, type PaStepKey } from '../lib/encounter-steps';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import { isSignalNurseEncounter, nurseRosLines, nurseRosOptions, NURSE_ROS_RISK_OPTIONS } from '../nurse-encounter-signal/nurse-encounter-rules';
import { riskBridgeScope } from '../risk/lib/risk-qr-bridge';
import { isPassableStep, useStepPass } from '../lib/step-pass';
import type { StepTitleOverrides } from '../lib/cps-layout';
import { toClinicalRole, type ShellRole } from '../lib/encounter-blocks';
import type { ApPart, SoapSectionId } from '../lib/soap-ribbon';
import {
  findNarrativeItem,
  stripItem,
  isQuestionnaireStep,
  mergeSectionResponse,
  questionnaireForStep,
  railGroups,
  sectionSymptomChipIds,
  withSymptomChipDefaults,
} from '../lib/questionnaire-groups';
import { findChiefComplaintHpiMarker } from '../lib/chief-complaint-hpi';
import { systemOrderFor, type ChiefComplaintRef } from '../complaint-map/system-order-for';
import type { NoteMacro } from '../lib/smart-phrase';
import {
  answerMap,
  applyUpdates,
  calculatedIds,
  chipState,
  chipValue,
  chipVariant,
  clearSection,
  examModel,
  joinNames,
  riskAllNeg,
  skippedNames,
  stepNarrative,
  noteItemAnchor,
  pickValue,
  rosChanges,
  rosFindings,
  ROS_STORABLE,
  rosModel,
  systemMatchesKey,
  drawnIds,
  hiddenIds,
  undoClear,
  undoFill,
  withoutDrawn,
  YES_NO_OPTIONS,
  isAnswered,
  pickOptions,
  yesNoValue,
  type AnswerMap,
  type AnswerUpdate,
  type NarrativeGroup,
  type NarrativeRow,
  type Refusal,
} from './note-adapter';
import {
  carriesPieces,
  caretAnchorIn,
  insertAt,
  placeAt,
  spliceAt,
  trackFieldCarets,
  triggerAnchorIn,
  type TriggerAnchor,
  lineCommandItems,
  lineEditors,
  offeredPhrasesIn,
  phrasePlainText,
  toCommandItems,
  toTemplateCommandItems,
  templateRowQuestionnaireId,
  type InsertableTemplate,
  toMenuMacros,
} from './note-phrases';
import { PhraseOccurrence } from './phrase-occurrence';
import { ComplaintLines } from './complaint-lines';
import { presentPrelude, riskGroupLinkIds, riskRecordGroups, riskRecordWrites, steadiFollowUpDrawn, type RiskRecordGroup, type RiskRecordRow } from './risk-record-lines';
import { RiskOptionalBlock } from './risk-optional-block';
import { insertPhrase, lineSegs, plainSegs, rebaseSegs, segsHavePieces, segsText, segsToDelta, type LineSeg } from '../lib/note-line-delta';
import { startPhraseFill, withFreshCharts, withRefreshedChart, type PhraseFillDraft } from './phrase-fill-model';
import { resolveChart, type ChartReads } from '../lib/to-chart-snapshot';
import type { ChartPiece } from '../lib/note-pieces';
import { scrollJumpTarget } from './jump-focus';
import { goToSignBlank, signBlanks } from './sign-blanks';
import { NoteDateLine } from './note-date-line';
import { suggestedFirst } from '../complaint-map/phrases-first';
import type { AlliedDiscipline } from '../lib/allied-discipline';
import { freeScreenScope, type RiskManaged } from '../risk/lib/risk-qr-bridge';
import {
  findingsChange,
  normalDescription,
  pickableFindings,
  positives,
  readText,
  restNegative,
  riskFindings,
  riskOptions,
  riskReadText,
  riskValue,
  riskWrite,
  stateChange,
  stateLabel,
  stateOptions,
  systemState,
  type SystemKind,
  type SystemLine,
  type SystemState,
} from './system-lines-model';
import { isDateItem, NOTE_DATE_FORMAT, toNoteDate } from './note-date-model';
import { NoteDetailText } from './note-detail-text';
import { ObjectiveVitals, VitalsRecordBody } from './objective-vitals';
import { drawsObjectiveVitals, OBJECTIVE_VITALS_TITLE, type VitalsReadState } from './objective-vitals-model';
import { chartFills, type ChartFill, type ChartLaunchContext } from '../lib/chart-population';
import { ChartFillContext } from './chart-fill-context';
import { ChartFilledLine } from './chart-filled-line';
import { HistoryNotesLine, type StructuredHistoryNote } from './history-notes-line';
import { NoteAddActionContext, NoteBlockActionsHostContext, NoteSignedContext, useNoteSigned, type AddNoteRequest } from './note-add-action';
import { NoteTextLine } from './note-text-line';
import { NoteLabel } from './label-parts';
import { NoteGroupTitleContext, StateTwinContext, isStateTwin, shownGroupTitle, useVisitNoteV2 } from './label-parts-model';
import { AddEntryButton } from '../add-entry';
import { ChipChoice } from './chip-choice';
import { alliedSection, alliedSoapLayout, withGroups } from '../lib/allied-soap';

/** The saved-phrase library and who is using it (the page's `useNoteMacros`, frame v2 only). */
export interface StructuredNotePhrases {
  macros: readonly NoteMacro[];
  /** Opens the existing Settings macro dialog, pre-filled with `text` ("Create macro from current
   *  data"). Absent → MacroMenu's create row does nothing but say where macros are made. */
  onCreateFromText?: (text: string) => void;
  /** PROD-2824: piece phrases are offered (and filled before they land) — the A&P note's
   *  `?ef=phrasePieces` switch, read by the page. Absent/false → only phrases without pieces. */
  pieces?: boolean;
  /** The page's chart reads, so a chart piece is quoted for THIS patient and visit (the same reads the
   *  A&P note's chart pieces use). Absent → a chart piece reads "not loaded yet". */
  chartReads?: ChartReads;
  /** THE CHART-PIECE HOOK (phrase-occurrence.tsx): how a chart link's values draw in a phrase
   *  occurrence. Absent → its R6 screen text. The chart-value card goes here. */
  renderChartPiece?: (piece: ChartPiece) => ReactNode;
  /** PROD-2947 Part 2 (G8): the triggers to SUGGEST first in a line answering to these editors — the starter-map order
   *  for the visit's chief complaint (complaint-map/phrases-first.ts). A menu order only: nothing is inserted. */
  suggestFor?: (editors: readonly string[]) => readonly string[];
  /** PROD-2947 (PROD-3055 b): an allied-health author's discipline, read from the visit's booked specialty — her lines
   *  answer to its phrase editor. Absent → all allied editors (allied authors only). */
  discipline?: AlliedDiscipline;
}

/** A step's `.` / `/` per note line, and the occurrence a piece phrase opens in place under that line. */
interface LineCommands {
  commandsFor: (linkId: string) => NoteFieldCommands | undefined;
  fillFor: (linkId: string) => ReactNode;
  /** The line's own write for a PLAIN edit: keeps every piece the edit does not touch (PROD-2961). */
  writeText: (linkId: string, text: string) => void;
}

export interface StructuredNoteSectionProps {
  section: { id: SoapSectionId; title: string };
  /** The working steps the open section holds, in the ribbon's order (Wrap-up last). */
  steps: readonly PaStepKey[];
  /** Long page: Wrap-up is its own card, drawn as a second block list after the section's other blocks. Absent → one list, as before. */
  wrapUpCard?: boolean;
  /** The whole nav, so the Sign list can name required blanks in every section. */
  navigableSteps: readonly PaStepKey[];
  role: ShellRole;
  signed?: boolean;
  questionnaire?: Questionnaire;
  stepTitles?: StepTitleOverrides;
  response: QuestionnaireResponse;
  /** The open section's ONE live response (EncounterShellView `sectionResponse`). */
  sharedResponseRef: MutableRefObject<QuestionnaireResponse>;
  onResponseChange: (r: QuestionnaireResponse) => void;
  onStepChange: (step: PaStepKey) => void;
  resolveChiefComplaint?: QuestionnaireRendererProps['resolveChiefComplaint'];
  resolveVitals?: QuestionnaireRendererProps['resolveVitals'];
  /** D-74 / D-45: the visit's vitals read state, for the Vitals block the frame draws first in Objective
   *  when the template carries no vitals-block marker (objective-vitals.tsx). Absent → ready. */
  vitalsStatus?: VitalsReadState;
  /** PROD-3040 · L1: what the visit's record holds, for the items the template declares are filled from the chart
   *  (SDC initialExpression / code binding). Absent → no item is filled; every item is asked as before. */
  chartContext?: ChartLaunchContext;
  chiefComplaints?: readonly ChiefComplaintRef[];
  phrases?: StructuredNotePhrases;
  /** The templates a "/" in a note line may add as an answer set of their own, and the way to add one. Absent → "/" offers none.
   *  Only the questionnaire steps' lines take them (HPI, Risk, ROS, PE), never the A&P, the history notes or the wrap-up. */
  insertableTemplates?: readonly InsertableTemplate[];
  onInsertTemplate?: (questionnaireId: string) => void;
  /** What a provider sign freezes (EncounterFrameV2 `signedFreeze`, PROD-2810 / rule 38). Absent: both
   *  follow `signed`. */
  signedFreeze?: { answers: boolean; records: boolean };
  /** PROD-2961: the line Deltas with pieces (committed), the live copy (for writing), and where a change
   *  goes. Absent → lines are plain text, exactly as before. */
  noteDeltas?: Readonly<Record<string, string>>;
  noteDeltasLive?: MutableRefObject<Readonly<Record<string, string>>>;
  onNoteDeltaChange?: (linkId: string, delta: string | undefined) => void;
  /** TS-3: the part of the A/P step this section draws (A: the assessment, P: the plan). Absent → the
   *  whole A/P, as before. */
  apPart?: ApPart;
  /** PROD-3042: the saved Fall / Pain assessment has merged into the response (the page's
   *  `riskBridge.ready`). Until then their lines are held back, as the card was. Absent → ready. */
  riskReady?: boolean;
  /** PROD-3042: which saved assessment could not be read (its edits are not saved). */
  riskReadFailed?: { fall: boolean; pain: boolean };
  /** PROD-3042: the patient's registration sex ('M' / 'F'), for the content's own sex marks. Absent: no sex on record. */
  patientSex?: string | null;
  /** PROD-3043 (D-73 amendment): the HPI narrative stored on the chief-complaint document ("History notes").
   *  Drawn only when the template has no HPI narrative item of its own. Absent → not drawn. */
  historyNote?: StructuredHistoryNote;
  /** PROD-2947 · 7 Oct 2026 ruling: Flutter's managed CDC STEADI / NRS lines in the Fall / Pain record, drawn read-only
   *  in the Risk block when the template does not draw that screen itself. */
  riskManaged?: RiskManaged;
  /** PROD-3128: the CDC STEADI follow-up drawn under "Fall risk present" (risk-record-lines.ts). Absent → none. */
  riskSteadi?: QItem;
  /** PROD-3142 (D-78): the Fall / Pain list refused as stale — read as text, written by nothing, until a reload. */
  riskFrozen?: { fall: boolean; pain: boolean };
  /** The host's existing canvas for a step, rendered inside its NoteBlock. `bare` drops the step's
   *  own section header (the NoteBlock already names the step); `frozen` draws it in review mode. */
  renderStepCanvas: (
    step: PaStepKey,
    opts: { bare: boolean; questionnaire?: Questionnaire; frozen?: boolean; apPart?: ApPart },
  ) => ReactNode;
}

/** S · O · A · P → the NoteSection letter (the ribbon's inks match: history · neutral · problems ·
 *  medications). */
const LETTER: Record<SoapSectionId, NoteSectionKey> = { s: 'S', o: 'O', a: 'A', plan: 'P' };

/** TS-3: the A/P step's block, per part — A writes the diagnoses, P the orders (A7, inline). */
const AP_PART_BLOCK: Record<ApPart, { title: string; icon: string }> = {
  // 19 Oct: 'assessment-plan' is not in the DS Icon registry (it drew a bare square, outside the wayfinding glyph rule);
  // 'section-plan' is a registered section glyph, and the bridge swaps the drawn shape per block (clipboard-list for Diagnoses).
  assessment: { title: 'Diagnoses', icon: 'section-plan' },
  plan: { title: 'Orders', icon: 'section-plan' },
};
/** The A/P block's title. `?x=plan-title` (Andrew, 4 Nov; ON by default on the dev hosts): the Plan block reads "Plan", as its step tab. */
const apPartTitle = (part: ApPart, planTitle: boolean): string => (part === 'plan' && planTitle ? 'Plan' : AP_PART_BLOCK[part].title);

/** Each step's block glyph — the DS encounter section glyphs (Icon registry). */
const ICON: Record<PaStepKey, string> = {
  hpi: 'section-hpi',
  risk: 'section-risk',
  ros: 'section-ros',
  pe: 'section-pe',
  docs: 'section-docs',
  ap: 'section-plan',
  'allied-plan': 'section-plan',
  'nursing-plan': 'section-plan',
  'wrap-up': 'section-wrapup',
};

/**
 * signal-ds 2.20 (#454, PROD-3009) frame v2 opt-ins, the audit's AA-contrast and 24px-target fixes. This
 * file renders only in frame v2, so they are passed unconditionally here; the switch-OFF frame never mounts
 * it. Quiet labels in text-secondary (5.53:1), controls at least 24px, bulk controls in context.
 */
const AA_LABELS = { labelTone: 'secondary' } as const;
const TARGET_24 = { targetSize: 24 } as const;
/** A template group's title WRAPS (template lane, #641; ENC-L12 / G4): the DS SectionEyebrow is `inline-flex
 *  whitespace-nowrap`, so a long title was cut at the block edge. DS ask: a wrapping option on SectionEyebrow. */
const GROUP_TITLE_WRAP = 'flex min-w-0 whitespace-normal break-words';
const FINDING_BLOCK = { labelTone: 'secondary', targetSize: 24, bulkControls: 'contextual' } as const;

/** The steps this file redraws with the structured blocks. Everything else keeps its canvas. */
const STRUCTURED: ReadonlySet<PaStepKey> = new Set<PaStepKey>(['hpi', 'risk', 'ros', 'pe']);

/** NoteBlock's author seat, from the signed-in user's role (the page's `role`, mapped as everywhere
 *  else in the app). The DS AuthorRole mirrors ClinicalRole (signal-ds #434), so an allied-health
 *  author reads "Allied Health", never "Physician". */
const authorOf = (role: ShellRole): AuthorRole => toClinicalRole(role);

const REFUSAL_TEXT: Record<Refusal['reason'], string> = {
  'bulk-overwrite': 'already recorded, so a bulk action left it as it was',
  'bulk-risk': 'a risk question, so it is never answered in bulk',
  'bulk-finding': 'a +/− finding, so "Normal on the rest" never sets it',
  'bulk-abnormal-system': 'its system has an abnormal finding, so "Normal on the rest" leaves it',
  'bulk-untouched-system': 'nothing is recorded in its system yet, so "Normal on the rest" leaves it',
};

/** A count (an integer note line) that is not a whole number of 0 or more: what is wrong and what to
 *  type (audit FV2-09: an invalid entry says why and gives the next step). */
export const COUNT_INVALID = 'Enter a whole number, 0 or more';
/** A decimal note line that is not a number. */
export const NUMBER_INVALID = 'Enter a number';

export function StructuredNoteSection(props: StructuredNoteSectionProps) {
  // `?x=plan-title`: read once per mount, as the other switches (#921 review).
  const [planTitleOn] = useState(() => isEncounterFlagOn('planTitle'));
  const { section, steps, role, stepTitles, response, sharedResponseRef, onResponseChange } = props;
  const [refusals, setRefusals] = useState<Refusal[]>([]);

  /** THE write: onto the shared response, then the page. Refusals become one visible notice. */
  const write = useCallback(
    (updates: readonly AnswerUpdate[], refused: readonly Refusal[] = []) => {
      setRefusals([...refused]);
      if (updates.length === 0) return;
      const next = applyUpdates(sharedResponseRef.current, updates);
      sharedResponseRef.current = next;
      onResponseChange(next);
    },
    [sharedResponseRef, onResponseChange],
  );
  /** Values read at the moment of a change (never a render-time copy), for the bulk guard. */
  const liveValues = useCallback((): AnswerMap => answerMap(sharedResponseRef.current), [sharedResponseRef]);

  const values = useMemo(() => answerMap(response), [response]);
  const author = authorOf(role);
  // [PROD-3376] Allied health: each template section coded with a SOAP section draws as its own card in that
  // section (lib/allied-soap.ts); the Risk card keeps only what is not coded. Any other role: nothing changes.
  const allied = useMemo(() => alliedSection(alliedSoapLayout(props.questionnaire, author), props.questionnaire, section.id), [props.questionnaire, author, section.id]);
  const sectionGroups = allied.groups;
  // PROD-2810 / rule 38: after a PROVIDER sign the note is changed only as an amendment. QR answers
  // freeze unless the clinician is amending; encounter-record writes (chief complaint, Fall / Pain)
  // freeze outright (the server refuses them with 409 and has no amendment path for them yet).
  const answersFrozen = props.signedFreeze?.answers ?? !!props.signed;
  const recordsFrozen = props.signedFreeze?.records ?? !!props.signed;
  // The section's summary line names its BLOCKS, with the blocks' own words (the A/P part's "Diagnoses" /
  // "Orders", "Documents", "Today's plan"), never the steps' nav labels (the independent review, 6 Oct).
  // D-71 (design desk, 6 Oct): the caption on the right of the section heading lists the section's blocks only
  // when it holds 2 or more; a one-block section (Assessment's "Diagnoses", the nurse's Wrap-up) shows none.
  // D-74 (design desk, 6 Oct): Objective always holds the Vitals block for the physician and the nurse —
  // at the template's vitals-block marker when it has one, otherwise first in Objective (drawn here).
  // PROD-3040 · L1 "Never ask what the chart already knows": the items of THIS section's steps whose answer the
  // record holds are filled from it, drawn read-only with "From the chart", and written to the response so a score
  // adds their points and the generated note documents them. Nothing in the record → the item stays askable.
  const fillKey = steps.join('|');
  const fills = useMemo<Readonly<Record<string, ChartFill>>>(() => {
    if (!props.chartContext || !props.questionnaire) return NO_FILLS;
    const q = props.questionnaire;
    const stepItems = (fillKey.split('|') as PaStepKey[]).flatMap((step) => (isQuestionnaireStep(step) ? questionnaireForStep(q, step).item : []));
    const items = [...stepItems, ...sectionGroups.filter((g) => !stepItems.includes(g))];
    const fresh = chartFills(items, props.chartContext);
    if (!recordsFrozen) return fresh;
    // After a PROVIDER signature (amending included) the note shows what was SAVED, never a fresh chart read: rule 2
    // freezes the narrative and the versions it referenced (Codex P1, 6 Oct). A saved answer that is the chart's value
    // keeps its "From the chart" provenance and stays read-only, even while amending (integration, Codex P2: it lost
    // its source and became an ordinary editable answer). An answer the chart no longer gives reads as saved.
    const kept: Record<string, ChartFill> = {};
    for (const [linkId, fill] of Object.entries(fresh)) if (JSON.stringify(values[linkId]) === JSON.stringify(fill.value)) kept[linkId] = fill;
    return Object.keys(kept).length > 0 ? kept : NO_FILLS;
  }, [props.chartContext, props.questionnaire, fillKey, recordsFrozen, values, sectionGroups]);
  const pendingFills = useMemo(
    () => Object.entries(fills).filter(([linkId, f]) => JSON.stringify(values[linkId]) !== JSON.stringify(f.value)),
    [fills, values],
  );
  useEffect(() => {
    // Frozen by a provider sign (rule 38): the record is shown, nothing is written. Onto the shared response and
    // the page, exactly as `write` does (a fill is never refused, so there is no notice to set).
    if (answersFrozen || recordsFrozen || pendingFills.length === 0) return;
    const next = applyUpdates(sharedResponseRef.current, pendingFills.map(([linkId, f]) => ({ linkId, value: f.value })));
    sharedResponseRef.current = next;
    onResponseChange(next);
  }, [answersFrozen, recordsFrozen, pendingFills, sharedResponseRef, onResponseChange]);
  const riskWithoutCoded = allied.riskQuestionnaire;
  // [PROD-3376] Allied health with a coded Objective: the same Vitals block opens Objective (the dietitian's body measures).
  const objectiveVitals =
    drawsObjectiveVitals({
      sectionId: section.id,
      author,
      questionnaire: props.questionnaire,
      navigableSteps: props.navigableSteps,
    }) ||
    allied.vitals;
  const blockTitles = [
    ...(objectiveVitals ? [OBJECTIVE_VITALS_TITLE] : []),
    ...sectionGroups.map((g) => g.text ?? ''),
    ...steps
      .filter((s) => !(s === 'risk' && allied.dropRisk))
      .map((s) => (s === 'ap' && props.apPart ? apPartTitle(props.apPart, planTitleOn) : noteBlockLabel(s, stepTitles))),
  ];
  const summary = blockTitles.length >= 2 ? blockTitles.join(' · ') : '';

  return (
    // The note column is the container D-62's label-column mode is measured on (signal-ds #444: no
    // side padding here, and w-full/min-w-0 so the container keeps its width inside a flex row).
    // The note spans the whole centre column (Ahmed 2026-10-07: min-width 100%, superseding FV2-07's
    // 760px cap). FV2-43: `relative` contains the note's absolutely placed screen-reader
    // text, which otherwise escaped the frame's scroller and let the whole page scroll by 244px.
    <div data-structured-note="on" className={`${NOTE_CONTAINER} relative w-full min-w-full`}>
      {/* No SOAP letter before the title (Ahmed 2026-10-07); `letter` still keys the section's wayfinding. */}
      <NoteSection letter={LETTER[section.id]} title={section.title} summary={summary} showLetter={false} {...AA_LABELS}>
        {refusals.length > 0 && (
          <Banner variant="warning" title="Not saved" onDismiss={() => setRefusals([])} data-testid="note-refusals">
            {refusals.map((r) => `${r.label}: ${REFUSAL_TEXT[r.reason]}.`).join(' ')}
          </Banner>
        )}
        {/* The host's block list keeps its hooks (`soap-section-blocks` > `data-soap-block`, #572), so
            the chair's frame tests and the parity probe read the same structure. 12 between cards
            (sheet §1 density, --spacing-3). */}
        <ChartFillContext.Provider value={fills}>
        {(() => {
          const wrapUpOwn = props.wrapUpCard === true && steps.includes('wrap-up');
          // [PROD-3376] One card per coded template section: the SAME Risk block, given only that section's group.
          const groupBlock = (g: QItem) =>
            props.questionnaire ? (
              <div key={`risk:${g.linkId}`} data-soap-block="risk" data-soap-group-card={g.linkId} className="flex flex-col">
                <StepBlock
                  {...props}
                  questionnaire={withGroups(props.questionnaire, (x) => x.linkId === g.linkId)}
                  blockTitle={g.text}
                  groupCard={g.linkId}
                  step="risk"
                  author={author}
                  values={values}
                  write={write}
                  liveValues={liveValues}
                  answersFrozen={answersFrozen}
                  recordsFrozen={recordsFrozen}
                />
              </div>
            ) : null;
          const block = (step: PaStepKey) => (
            // TS-3: the A/P block is keyed by its part, so A and P are two instances — a form, the
            // search tab or a handed-over order never carries from one part into the other.
            <div
              key={step === 'ap' && props.apPart ? `${step}:${props.apPart}` : step}
              data-soap-block={step}
              data-ap-part={step === 'ap' ? props.apPart : undefined}
              className="flex flex-col"
            >
              <StepBlock
                {...props}
                {...(step === 'risk' && riskWithoutCoded ? { questionnaire: riskWithoutCoded } : {})}
                step={step}
                author={author}
                values={values}
                write={write}
                liveValues={liveValues}
                answersFrozen={answersFrozen}
                recordsFrozen={recordsFrozen}
              />
            </div>
          );
          const ownSteps = allied.dropRisk ? steps.filter((s) => s !== 'risk') : steps;
          const main = wrapUpOwn ? ownSteps.filter((s) => s !== 'wrap-up') : ownSteps;
          return (
            <>
              {(objectiveVitals || main.length > 0 || sectionGroups.length > 0) && (
                <div data-slot="soap-section-blocks" className="flex flex-col gap-3">
                  {objectiveVitals && (
                    <div key="vitals" data-soap-block="vitals" className="flex flex-col">
                      <ObjectiveVitals resolve={props.resolveVitals} state={props.vitalsStatus} frozen={recordsFrozen} />
                    </div>
                  )}
                  {sectionGroups.map(groupBlock)}
                  {main.map(block)}
                </div>
              )}
              {wrapUpOwn && (
                <div data-slot="soap-section-blocks" data-card="wrap-up" className="flex flex-col gap-3">
                  {block('wrap-up')}
                </div>
              )}
            </>
          );
        })()}
        </ChartFillContext.Provider>
      </NoteSection>
    </div>
  );
}

interface StepBlockProps extends StructuredNoteSectionProps {
  step: PaStepKey;
  /** [PROD-3376] A coded template section drawn as its own card: the card is named for the section. */
  blockTitle?: string;
  /** [PROD-3376] That section's group linkId: the card's own id (`note-block-risk-<linkId>`). */
  groupCard?: string;
  author: AuthorRole;
  values: AnswerMap;
  write: (updates: readonly AnswerUpdate[], refused?: readonly Refusal[]) => void;
  liveValues: () => AnswerMap;
  /** The note's QR answers are frozen (a provider sign, not amending). */
  answersFrozen: boolean;
  /** The encounter-record writes are frozen (a provider sign). */
  recordsFrozen: boolean;
}

function StepBlock(p: StepBlockProps) {
  const { step, questionnaire, author, stepTitles, values, answersFrozen, recordsFrozen } = p;
  const part = step === 'ap' ? p.apPart : undefined;
  // `?x=plan-title`: read once per mount (#921 review).
  const [planTitleOn] = useState(() => isEncounterFlagOn('planTitle'));
  const title = part ? apPartTitle(part, planTitleOn) : (p.blockTitle ?? noteBlockLabel(step, stepTitles));
  const sectionQ = useMemo(
    () => (questionnaire && isQuestionnaireStep(step) ? questionnaireForStep(questionnaire, step) : undefined),
    [questionnaire, step],
  );
  const narrativeItem = useMemo(() => (sectionQ ? findNarrativeItem(sectionQ.item) : undefined), [sectionQ]);
  const structured = STRUCTURED.has(step) && !!sectionQ && sectionQ.item.length > 0;
  // PROD-2810 / rule 38 (supersedes the 6 Aug "HPI never disabled" register row): every step's fields
  // are editable until the PROVIDER signs.
  const fieldsReadOnly = answersFrozen;
  // PROD-2824 (Codex P1, round 1): phrases follow the line's own editability — a frozen ROS / PE line
  // offers no menu, no fill panel and no Macros, and an open panel closes when the note is signed.
  const fieldCommands = useLineCommands(p, step, author, fieldsReadOnly);
  // The section narrative is a NoteFieldLine like every free-text item (judge J2): `.` and `/` open the
  // phrase library inside it. Macros is the block's action, not a box's.
  // FROZEN = NO WRITE (Andrew's review on #587; PROD-2810 / rule 38): after the provider sign the
  // narrative reads as text and Macros ("Insert phrase") is absent, unless the clinician is amending.
  // L10 (structure first): empty, the narrative draws nothing and the header offers "Add note".
  const [addNote, setAddNote] = useState<AddNoteRequest | null>(null);
  // `visitNoteV2` (Andrew, 4 Nov): "Add note" and Macros top right in the head (overriding PROD-3391's end row), and no "Nothing to
  // record" on the cards. A pass is display only (lib/step-pass.ts: no sign gate, no save, kept in this browser), so no block
  // becomes unsignable; a pass already stored still shows on the step bar and ends when its step is answered.
  const noteV2 = useVisitNoteV2();
  // Export 21-10 (EncounterDocumentsStep): the Documents list draws its Add order / Add document in this header.
  const [docsActionsHost, setDocsActionsHost] = useState<HTMLSpanElement | null>(null);
  const narrative = narrativeItem ? (
    <div key={narrativeItem.linkId} className="flex flex-col gap-2">
      <NoteTextLine
        start="block"
        id={noteItemAnchor(narrativeItem.linkId)}
        label={narrativeItem.text ?? 'Notes'}
        value={typeof values[narrativeItem.linkId] === 'string' ? (values[narrativeItem.linkId] as string) : ''}
        onChange={(text) => fieldCommands.writeText(narrativeItem.linkId, text)}
        // Frozen by the provider sign (PROD-2810 / rule 38) unless amending: no phrase menu, no fill panel.
        readOnly={answersFrozen}
        commands={answersFrozen ? undefined : fieldCommands.commandsFor(narrativeItem.linkId)}
      />
      {!answersFrozen && fieldCommands.fillFor(narrativeItem.linkId)}
    </div>
  ) : null;
  // PROD-3043 (D-73 amendment): "History notes" (the chief-complaint comment) in the HPI block. One narrative
  // line per block: a template that carries its own HPI narrative item keeps that item, and this is not drawn.
  const historyNotes =
    step === 'hpi' && !narrativeItem && p.historyNote ? (
      <HistoryNotesLine note={p.historyNote} author={author} frozen={recordsFrozen} phrases={p.phrases} />
    ) : null;
  // PROD-3149 part 1: Macros sits in the block header, but its preview opens in the block body, under the
  // narrative (where the R6c screen draws it, and where the phrase lands) — never inside the header row.
  const [macroPreview, setMacroPreview] = useState<HTMLDivElement | null>(null);
  const macros =
    narrativeItem && !answersFrozen ? (
      <BlockMacros
        {...p}
        item={narrativeItem}
        onFill={fieldCommands.openFill}
        onWriteText={fieldCommands.writeText}
        readOnly={fieldsReadOnly}
        previewContainer={macroPreview}
      />
    ) : null;

  const riskTools = useRiskBulk(p, step === 'risk' ? sectionQ : undefined, title);
  const skip = useSkip(narrativeItem?.linkId);
  const structuredStep = step === 'hpi' || step === 'risk' || step === 'ros' || step === 'pe' ? step : undefined;
  // The titles drawn above the step (D-71 amendment): a template group repeating one is not drawn again.
  const repeatKey = titleRepeats(p).join('\u0000');
  const repeats = useMemo(() => repeatKey.split('\u0000'), [repeatKey]);
  const split = useMemo(
    () => (structured && sectionQ && structuredStep ? stepNarrative(structuredStep, sectionQ, values, skip, repeats) : NO_SPLIT),
    [structured, sectionQ, structuredStep, values, skip, repeats],
  );
  // Risk: the canvas below the block draws everything the block does not (Fall / Pain, no-line items).
  // Keyed on WHAT the block draws (not on every answer), so the canvas keeps one questionnaire
  // identity across ordinary edits (the DS renderer memoises on it).
  // F8 (live dev, 6 Oct): a Risk item hidden by its enableWhen draws nowhere, so it counts as drawn here —
  // otherwise the canvas draws its group as an empty second card repeating the group title (L9).
  const hiddenKey = useMemo(() => (step === 'risk' && sectionQ ? hiddenIds(sectionQ.item, values).join('\u0000') : ''), [step, sectionQ, values]);
  const drawnKey = useMemo(
    () => [...drawnIds(split.inBlock, skip), ...(hiddenKey ? hiddenKey.split('\u0000') : [])].join('\u0000'),
    [split.inBlock, skip, hiddenKey],
  );
  const riskCanvasQ = useMemo(
    () => (step === 'risk' && structured && questionnaire ? withoutDrawn(questionnaire, new Set(drawnKey.split('\u0000'))) : undefined),
    [step, structured, questionnaire, drawnKey],
  );
  const riskCanvasHasItems = !!riskCanvasQ && questionnaireForStep(riskCanvasQ, 'risk').item.length > 0;
  // Codex P2 (integration review, 6 Oct): the canvas holds two kinds of answer with two freeze rules (rule 38).
  // Fall / Pain live in the encounter record: frozen at the provider sign, amending or not. Anything else in
  // the canvas (a Risk date or attachment with no note line yet) is a QR answer: it reopens while amending.
  // So the canvas draws as two parts, each with its own freeze; both write through the one shared response.
  const riskParts = useMemo(() => {
    if (!riskCanvasQ || !riskCanvasHasItems) return undefined;
    const records = riskBridgeScope(questionnaireForStep(riskCanvasQ, 'risk').item).linkIds;
    const leaves: string[] = [];
    const walk = (list: readonly QItem[]) => list.forEach((it) => (it.type === 'group' ? walk(it.item ?? []) : leaves.push(it.linkId)));
    walk(riskCanvasQ.item);
    const recordQ = withoutDrawn(riskCanvasQ, new Set(leaves.filter((id) => !records.has(id))));
    // K7 (PROD-2947): a QR date in the canvas is drawn by the frame as a date line ("Add" opens the DS date picker),
    // never as the renderer's date box in a card; the renderer keeps only what has no line at all.
    const dates: QItem[] = [];
    const findDates = (list: readonly QItem[]) =>
      list.forEach((it) => (it.type === 'group' ? findDates(it.item ?? []) : !records.has(it.linkId) && isDateItem(it) && dates.push(it)));
    findDates(riskCanvasQ.item);
    const answersQ = withoutDrawn(riskCanvasQ, new Set([...records, ...dates.map((d) => d.linkId)]));
    const has = (q: Questionnaire) => questionnaireForStep(q, 'risk').item.length > 0;
    return { recordQ: has(recordQ) ? recordQ : undefined, answersQ: has(answersQ) ? answersQ : undefined, dates };
  }, [riskCanvasQ, riskCanvasHasItems]);
  // G8 (PROD-2947): the Risk block draws its groups in the template's order. Fall / Pain (the canvas's record part) come
  // before the block's other lines when the template authors them first (the live physician template: Fall, Pain, CDC).
  const recordsFirst = useMemo(() => {
    if (step !== 'risk' || !sectionQ || !riskParts?.recordQ) return false;
    const scope = riskBridgeScope(sectionQ.item);
    const firstRecord = [scope.fall, scope.pain].filter(Boolean).map((g) => authoredIndex(sectionQ.item, (g as QItem).linkId));
    const firstLine = split.inBlock.map((g) => authoredIndex(sectionQ.item, g.linkId));
    return firstRecord.length > 0 && Math.min(...firstRecord) < Math.min(Number.MAX_SAFE_INTEGER, ...firstLine);
  }, [step, sectionQ, riskParts, split.inBlock]);
  const fieldRun = useFieldRun(p, title, fieldsReadOnly);

  let body: ReactNode;
  const bodyProps = { ...p, narrative: split.inBlock, fieldCommands };
  // The section's narrative is drawn at the END of the block (below), wherever the template puts it. Beside the systems
  // (not inside the last one) it used to stop the system walk descending into a lone wrapper, and the whole exam drew as
  // ONE row (template lane, #641): the systems are read from the section WITHOUT it.
  const systemsQ = useMemo(() => (sectionQ && narrativeItem ? stripItem(sectionQ, narrativeItem.linkId) : sectionQ), [sectionQ, narrativeItem]);
  if (structured && sectionQ && step === 'hpi') body = <HpiBody {...bodyProps} sectionQ={sectionQ} />;
  else if (structured && systemsQ && step === 'ros') body = <RosBody {...bodyProps} sectionQ={systemsQ} />;
  else if (structured && systemsQ && step === 'pe') body = <ExamBody {...bodyProps} sectionQ={systemsQ} />;
  else if (structured && sectionQ && step === 'risk') body = <RiskBody {...bodyProps} riskSystems={split.riskSystems} />;
  // `bare`: the block names the step, so the canvas draws no title of its own — the questionnaire steps'
  // section header, and Wrap-up's DS header title (design desk, 5 Oct: each title once).
  else body = p.renderStepCanvas(step, { bare: isQuestionnaireStep(step) || step === 'wrap-up', ...(part ? { apPart: part } : {}) });

  // L10: one quiet "Add note" in the header while the block's narrative is empty and editable (never after the
  // provider's signature, amending or not: rule 38).
  const addNoteAction =
    addNote && !recordsFrozen ? (
      <AddEntryButton onClick={addNote.open} data-add-note id={addNote.anchor}>
        Add note
      </AddEntryButton>
    ) : null;
  // "Nothing to record" / "Undo" for a block the ✓ rules read; hidden after the provider sign.
  const stepPass = useStepPass();
  const [wrapUpList] = useState(() => isEncounterFlagOn('wrapUpList'));
  const passAction =
    stepPass && !recordsFrozen && isPassableStep(step) && (!part || part === 'assessment') ? (
      <Button variant="tertiary" size="sm" data-pass-action onClick={() => stepPass.onToggle(step)}>
        {stepPass.passed.has(step) ? 'Undo' : 'Nothing to record'}
      </Button>
    ) : null;
  return (
    <NoteSignedContext.Provider value={recordsFrozen}>
    <NoteAddActionContext.Provider value={setAddNote}>
    <NoteBlockActionsHostContext.Provider value={step === 'docs' ? docsActionsHost : null}>
    <NoteBlock
      id={part ? `note-block-${step}-${part}` : p.groupCard ? `note-block-${step}-${p.groupCard}` : `note-block-${step}`}
      title={title}
      // D-69 (signal-ds 2.20, #454): frame v2 block titles in text-primary; the SOAP colour stays in the
      // tile and the rule line. FV2-19 card border; FV2-02 the header's quiet text in text-secondary.
      // No author / state label on the blocks (Ahmed 2026-10-07, superseding FV2-33's "Physician · draft"):
      // NoteBlock draws it only from `author` / `status` / `signed`, so none is passed. The note's sign
      // state lives in the header (sign button and the signed line).
      titleTone="text-primary"
      border="card"
      {...AA_LABELS}
      icon={part ? AP_PART_BLOCK[part].icon : ICON[step]}
      placement="wayfinding"
      actions={
        riskTools.actions || addNoteAction || macros || (!noteV2 && passAction) || step === 'docs' ? (
          <span className="flex items-center gap-2">
            {step === 'docs' ? <span ref={setDocsActionsHost} data-slot="note-block-docs-actions" className="flex items-center gap-2 empty:hidden" /> : null}
            {!noteV2 && passAction}
            {/* Andrew, 4 Nov (overrides PROD-3391 for Add note): "put it in the top right of the sections as before, not at the
                bottom". So "Add note" stays in the head with the switch on too, and Macros sits beside it (Andrew, 4 Nov). */}
            {addNoteAction}
            {noteV2 && macros}
            {riskTools.actions}
            {!noteV2 && macros}
          </span>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-4">
        {/* `wrapUpList` ON (PROD-3400): no "Before you sign" here; the floating sign bar lists what is missing (PR 900). The bar is drawn
            on the catalog note only (`visitNoteV2`), so the banner goes only when both are on: the blanks are never listed nowhere. */}
        {step === 'wrap-up' && !(wrapUpList && noteV2) && <SignList {...p} />}
        {riskTools.notice}
        {!recordsFirst && body}
        {step === 'risk' && riskCanvasHasItems && (
          // The Risk canvas INSIDE the Risk block (the independent review, 6 Oct: no "More Risk" area and
          // no empty Risk block above it): Fall / Pain (bridged to EncounterMetaData, held back until
          // their saved assessment loads, with Done) and anything with no note line yet. It draws only
          // what the block's lines do not, so each answer keeps one writer.
          <div className="flex flex-col gap-2" data-outside-fields="risk">
            {riskParts?.recordQ && (
              <>
                {/* PROD-3042 (F7): Fall / Pain as note lines, not the renderer's cards. They live in the encounter
                    record, which has no amendment path yet: frozen at the provider sign, amending or not (rule 38). */}
                <RiskRecords {...p} recordQ={riskParts.recordQ} />
              </>
            )}
            {/* K7: the canvas's QR dates as date lines — frozen like every note answer, reopened while amending. */}
            {riskParts?.dates.map((d) => (
              <NoteDateLine
                key={d.linkId}
                id={noteItemAnchor(d.linkId)}
                label={d.text ?? d.linkId}
                value={values[d.linkId]}
                readOnly={answersFrozen || d.readOnly === true}
                onChange={(date) => p.write([{ linkId: d.linkId, value: date }])}
              />
            ))}
            {/* The canvas's QR answers (no note line yet): frozen like every note answer — reopened while amending. */}
            {riskParts?.answersQ && p.renderStepCanvas('risk', { bare: true, questionnaire: riskParts.answersQ, frozen: answersFrozen })}
          </div>
        )}
        {step === 'risk' && sectionQ && p.riskManaged && (
          // G7: a screen the template authors ANYWHERE in the note holds the fact (Codex); the managed line stands in only
          // when the template has no such screen at all.
          <ManagedRiskLines
            managed={p.riskManaged}
            items={p.questionnaire?.item ?? sectionQ.item}
            followUp={steadiFollowUpDrawn(p.questionnaire?.item ?? sectionQ.item, values, p.riskSteadi)}
          />
        )}
        {recordsFirst && body}
        {step !== 'risk' && split.outside.length > 0 && (
          // D-70 (design desk, 6 Oct): items with no Structured Note v2 line yet (dates, attachments, optional
          // numbers, scored instruments) draw INSIDE their own block, after its lines — no "More <block>"
          // eyebrow, no separate card. The same rule as the Risk canvas above. Same answers, same save path.
          <div className="flex flex-col gap-2" data-outside-fields={step}>
            {split.outside.flatMap((g) =>
              // K7 (PROD-2947): a date in the run is a date line; the rest of the run keeps the DS field, split around it.
              dateRuns(g).map((run) =>
                isDateItem(run) ? (
                  <NoteDateLine
                    key={run.linkId}
                    id={noteItemAnchor(run.linkId)}
                    label={run.text ?? run.linkId}
                    value={values[run.linkId]}
                    readOnly={answersFrozen || run.readOnly === true}
                    onChange={(date) => p.write([{ linkId: run.linkId, value: date }])}
                  />
                ) : (
                  // One authored group can yield several runs (lines between them): key by group + first item.
                  // `data-outside-items`: the run's item linkIds, so a Sign-list name can find (and focus) the
                  // run that draws its item — the renderer tags no leaf control with its linkId (audit FV2-22).
                  <div key={`${run.linkId}:${run.item?.[0]?.linkId ?? ''}`} data-outside-items={(run.item ?? []).map((i) => i.linkId).join(' ')}>
                    {fieldRun(run)}
                  </div>
                ),
              ),
            )}
          </div>
        )}
        {narrative}
        {/* The Macros preview's place (PROD-3149). Empty, it takes no room; `-mt-4` cancels the column's gap so the
            preview keeps its own 16px, as in R6c. */}
        {macros && <div ref={setMacroPreview} className="-mt-4 empty:hidden" data-macro-preview />}
        {historyNotes}
      </div>
    </NoteBlock>
    </NoteBlockActionsHostContext.Provider>
    </NoteAddActionContext.Provider>
    </NoteSignedContext.Provider>
  );
}

const NO_SPLIT: { inBlock: NarrativeGroup[]; outside: QItem[]; riskSystems: Record<string, RosSystem> } = { inBlock: [], outside: [], riskSystems: {} };

/** An outside run split around its date items (K7): each date item alone, the other items in runs of their group. */
function dateRuns(group: QItem): QItem[] {
  const items = group.item ?? [];
  if (!items.some(isDateItem)) return [group];
  const out: QItem[] = [];
  let run: QItem[] = [];
  const flush = () => {
    if (run.length > 0) out.push({ ...group, item: run });
    run = [];
  };
  for (const it of items) {
    if (isDateItem(it)) {
      flush();
      out.push(it);
    } else run.push(it);
  }
  flush();
  return out;
}

/** Where `linkId` sits in the template, depth first (an unknown linkId sorts last). */
function authoredIndex(items: readonly QItem[], linkId: string): number {
  let i = 0;
  let at = -1;
  const walk = (list: readonly QItem[]) => {
    for (const it of list) {
      if (at >= 0) return;
      if (it.linkId === linkId) at = i;
      i += 1;
      walk(it.item ?? []);
    }
  };
  walk(items);
  return at < 0 ? Number.MAX_SAFE_INTEGER : at;
}
const NO_FILLS: Readonly<Record<string, ChartFill>> = {};


// ── Risk: All Neg and Clear (contract G7) ────────────────────────────────────────────────────

/**
 * The Risk block's All Neg and Clear, in the NoteBlock header. With frame v2 the Risk canvas draws
 * without its old section header, which is where OFF keeps these two, so they live here instead.
 *  · All Neg (G7): offered only when the section has a +/- finding and the note is not signed (OFF's
 *    rule). It is #559's `fillSectionNegative` (G7, one implementation): UNANSWERED findings only,
 *    never a risk item (`riskAllNeg` reports what it wrote); each skipped risk
 *    item is named in the "Not saved" notice. "Undo" puts back exactly what it set, unless changed since.
 *  · Clear: OFF's scope (every answer under the section's linkIds, its narrative too — what OFF's
 *    merge-with-nothing removes), offered where OFF offers it (the section has groups). It asks first,
 *    as the DS renderer's Clear does; OFF's chrome Clear does not.
 * Both go through the ONE shared response; Risk answers still reach the page's Fall/Pain bridge
 * through `onResponseChange`, exactly as a renderer edit does.
 */
function useRiskBulk(p: StepBlockProps, sectionQ: Questionnaire | undefined, title: string) {
  const [confirming, setConfirming] = useState(false);
  const [filled, setFilled] = useState<{ updates: AnswerUpdate[]; skipped: string[] } | null>(null);
  // The answers a Clear removed, as they were before it, so it can be undone (audit FV2-23).
  const [cleared, setCleared] = useState<AnswerUpdate[] | null>(null);
  // A new template (another questionnaire under the same block) drops any open confirm and every Undo:
  // they were made against the old template's items and must never restore its answers (Codex P2).
  const [forTemplate, setForTemplate] = useState(sectionQ);
  if (forTemplate !== sectionQ) {
    setForTemplate(sectionQ);
    setConfirming(false);
    setFilled(null);
    setCleared(null);
  }
  // Focus never drops to the page as the inline confirm comes and goes (audit FV2-23; Codex P2):
  //  · the confirm opens with focus on its SAFE choice, Cancel (it used to land on "Clear", one Enter
  //    away from wiping every Risk answer);
  //  · Cancel returns focus to "Clear Risk"; Clear moves it to the Undo it offers.
  const cancelRef = useRef<HTMLButtonElement>(null);
  const clearTriggerRef = useRef<HTMLButtonElement>(null);
  const undoClearRef = useRef<HTMLButtonElement>(null);
  // A focus request is set by a handler (which also changes state, so a render follows) and honoured
  // once, after that render has put the target on screen.
  const focusNextRef = useRef<'cancel' | 'trigger' | 'undo' | null>(null);
  const setFocusNext = (to: 'cancel' | 'trigger' | 'undo') => {
    focusNextRef.current = to;
  };
  useEffect(() => {
    const to = focusNextRef.current;
    if (to === null) return;
    focusNextRef.current = null;
    (to === 'cancel' ? cancelRef.current : to === 'trigger' ? clearTriggerRef.current : undoClearRef.current)?.focus();
  });
  if (!sectionQ || sectionQ.item.length === 0) return { actions: undefined, notice: null };
  const offerAllNeg = !p.signed && sectionSymptomChipIds(sectionQ, p.response).length > 0;
  // Clear is a write too, and it reaches the Fall / Pain record: never offered once a provider has
  // signed (Andrew's review, 6 Oct; rule 38), like All Neg above. With neither offered the hook
  // returns nothing, so an Undo or an open Clear confirm from before the sign goes with them.
  const offerClear = !p.recordsFrozen && railGroups(sectionQ).length > 0;
  if (!offerAllNeg && !offerClear) return { actions: undefined, notice: null };

  // PROD-3142 (D-78): a Fall / Pain list frozen after a stale save is left exactly as it is by Clear. (All Neg fills
  // +/- finding chips only, never a Fall / Pain record item.)
  const frozenIds = riskGroupLinkIds(sectionQ.item, p.riskFrozen);
  // PROD-3145 (desk CHANGES on #661 at d53eb725, 9 Oct): the CDC STEADI follow-up the page adds under Fall is part of
  // Risk, so Clear clears its answers too, counts them, and Undo restores them. Frozen with the Fall list after a 412,
  // it is left exactly as it is, like the list itself.
  const steadiIds = new Set<string>();
  const walkSteadi = (it: QItem) => {
    steadiIds.add(it.linkId);
    (it.item ?? []).forEach(walkSteadi);
  };
  if (p.riskSteadi) walkSteadi(p.riskSteadi);
  const unfrozen = (us: readonly AnswerUpdate[]) =>
    us.filter((u) => !frozenIds.has(u.linkId) && !(p.riskFrozen?.fall && steadiIds.has(u.linkId)));
  const allNeg = () => {
    const { updates, refused } = riskAllNeg(p.sharedResponseRef.current, sectionQ);
    // The skipped risk items are named in the result line, by their group (desk, 2 Oct), not in a
    // separate "Not saved" notice: a risk question is never a failure, it is the clinician's to ask.
    p.write(updates);
    const skipped = skippedNames(sectionQ.item, refused);
    // A second All Neg that sets nothing keeps the first one's Undo (Codex P2, 2 Oct).
    setFilled((prev) => (updates.length === 0 && prev && prev.updates.length > 0 ? prev : { updates, skipped }));
    setCleared(null);
  };
  const undo = () => {
    p.write(undoFill(filled?.updates ?? [], p.liveValues()));
    setFilled(null);
  };
  const clear = () => {
    const live = p.liveValues();
    const updates = unfrozen(clearSection(p.riskSteadi ? [...sectionQ.item, p.riskSteadi] : sectionQ.item, live));
    p.write(updates);
    setConfirming(false);
    setFilled(null);
    // What it removed, as it was, for Undo; a Clear that removed nothing offers nothing to undo.
    setCleared(updates.length > 0 ? updates.map((u) => ({ linkId: u.linkId, value: live[u.linkId] })) : null);
    setFocusNext(updates.length > 0 ? 'undo' : 'trigger');
  };
  const undoClearing = () => {
    p.write(undoClear(cleared ?? [], p.liveValues()));
    setCleared(null);
    setFocusNext('trigger');
  };

  const actions = confirming ? (
    <span className="flex items-center gap-2" role="group" aria-label={`Clear ${title}?`}>
      <span className="font-sans text-sm text-text-secondary">Clear every {title} answer?</span>
      <Button variant="tertiary" size="sm" onClick={clear}>
        Clear
      </Button>
      <Button
        ref={cancelRef}
        variant="tertiary"
        size="sm"
        onClick={() => {
          setConfirming(false);
          setFocusNext('trigger');
        }}
      >
        Cancel
      </Button>
    </span>
  ) : (
    <span className="flex items-center gap-2">
      {offerAllNeg && (
        <Button variant="tertiary" size="sm" onClick={allNeg}>
          All Neg
        </Button>
      )}
      {offerClear && (
        <Button
          ref={clearTriggerRef}
          variant="tertiary"
          size="sm"
          onClick={() => {
            setConfirming(true);
            setFocusNext('cancel');
          }}
        >
          Clear {title}
        </Button>
      )}
    </span>
  );
  const n = filled?.updates.length ?? 0;
  const skippedLine =
    filled && filled.skipped.length > 0
      ? ` ${joinNames(filled.skipped)} ${filled.skipped.length === 1 ? 'is' : 'are'} not set in bulk: ask directly.`
      : '';
  const c = cleared?.length ?? 0;
  const notice = filled ? (
    <p className="m-0 flex items-center gap-2 font-sans text-sm text-text-secondary" data-testid="risk-bulk-notice">
      {(n > 0
        ? `All Neg recorded ${n} ${n === 1 ? 'finding' : 'findings'}.`
        : 'All Neg recorded nothing new: every finding it may set is already answered.') + skippedLine}
      {n > 0 && (
        <Button variant="tertiary" size="sm" onClick={undo}>
          Undo
        </Button>
      )}
    </p>
  ) : cleared ? (
    <p className="m-0 flex items-center gap-2 font-sans text-sm text-text-secondary" data-testid="risk-bulk-notice">
      {`Clear removed ${c} ${title} ${c === 1 ? 'answer' : 'answers'}.`}
      <Button ref={undoClearRef} variant="tertiary" size="sm" onClick={undoClearing}>
        Undo
      </Button>
    </p>
  ) : null;
  return { actions, notice };
}

// ── Rows ─────────────────────────────────────────────────────────────────────────────────────

/** The note's label | value line: the DS NoteLine (one label column for the whole note, D-61), with
 *  a one-line control (a chip, a blank) centred on the label. `id` is the Sign-list anchor. A value
 *  that can run to two lines (the chief complaint) passes `align="start"`: its label tops the value's
 *  first line (D-61 (A); audit FV2-32), never its middle. */
function Row({ id, label, align = 'center', stateTwin, children }: { id?: string; label: ReactNode; align?: 'center' | 'start'; stateTwin?: boolean; children: ReactNode }) {
  return (
    <div id={id} data-note-row>
      <NoteLine label={<NoteLabel label={label} stateTwin={stateTwin} />} align={align} valueClassName="flex flex-wrap items-center gap-2">
        {children}
      </NoteLine>
    </div>
  );
}

/**
 * A Risk-step template `choice` with at least this many answer options (more than 8) draws as the one-line closed
 * pick list (DS PickListChip); with 8 or fewer it keeps the visible chips, exactly as before.
 * Sources: the note interaction contract K1 ("closed list, at most 8 rows") and Antonin's 19 Oct ruling that short
 * lists show clickable chips (commit 55977fb7). Encounter owner's ruling, 2 Nov (PROD-3376).
 */
export const PICK_LIST_MIN_OPTIONS = 9;

interface RowsProps {
  rows: readonly NarrativeRow[];
  values: AnswerMap;
  readOnly: boolean;
  write: StepBlockProps['write'];
  /** `.` / `/` inside each NoteFieldLine (D-61 ruling C): the step's phrase library, per line, and the
   *  fill panel a piece phrase opens under its line (PROD-2824). */
  commands?: LineCommands;
  /** The Risk step (note interaction contract K1/K2): a template `choice` item with `PICK_LIST_MIN_OPTIONS` or more
   *  options is ONE line — its label, then the DS closed pick list (value as text, or "Not asked · select"; at most 8
   *  rows, type to filter; multi-select reads as comma text). Fewer options, or absent → the visible chips (ChipChoice),
   *  as before. "+/-" finding chips never come through here. */
  pickList?: boolean;
}

/** The note's lines: chips where the template codes the answer, NoteFieldLine for free text.
 *  Renderer runs never reach here: `stepNarrative` sends them outside the block. */
export function NarrativeRows({ rows, values, readOnly, write, commands, pickList = false }: RowsProps) {
  const filled = useContext(ChartFillContext);
  // The linkIds on these lines, so a "-state" twin can tell its base item is here (label-parts-model.ts `isStateTwin`).
  const lineIds = new Set(rows.flatMap((r) => ('item' in r ? [r.item.linkId] : [])));
  return (
    <>
      {rows.map((row) => {
        if (row.kind === 'fields') return null;
        if (row.kind === 'chips') {
          return (
            <div key={`chips:${row.items[0].linkId}`} className="flex flex-wrap items-center gap-2 py-1.25" data-note-chips>
              {row.items.map((it) => (
                <span key={it.linkId} id={noteItemAnchor(it.linkId)}>
                  <SymptomChip
                    label={it.text ?? it.linkId}
                    variant={chipVariant(it)}
                    value={chipState(it, values[it.linkId])}
                    onChange={(next) => write([{ linkId: it.linkId, value: chipValue(it, next) }])}
                    readOnly={readOnly}
                    {...TARGET_24}
                  />
                </span>
              ))}
            </div>
          );
        }
        const { item } = row;
        const label = item.text ?? item.linkId;
        const anchor = noteItemAnchor(item.linkId);
        // PROD-3040 · L1: an item the chart answers is never asked — read-only, with its source.
        const fill = filled[item.linkId];
        if (fill && row.kind !== 'text') {
          return <ChartFilledLine key={item.linkId} id={anchor} item={item} label={label} fill={fill} />;
        }
        if (row.kind === 'text') {
          return (
            <p key={item.linkId} id={anchor} className="m-0 font-sans text-sm leading-6 text-text-secondary">
              {label}
            </p>
          );
        }
        if (row.kind === 'pick') {
          const v = pickValue(row, values[item.linkId]);
          const required = item.required ? label : undefined;
          const detail = row.detail;
          const detailValue = detail ? values[detail.linkId] : undefined;
          return (
            <Row key={item.linkId} id={anchor} label={label} align="start" stateTwin={isStateTwin(item.linkId, lineIds)}>
              {pickList && row.options.length >= PICK_LIST_MIN_OPTIONS ? (
                // K1 / K2: one line, one closed list (the DS PickListChip, as the phrase pick list uses it).
                row.multiple ? (
                  <PickListChip
                    multiple
                    options={row.options}
                    value={Array.isArray(v) ? v : []}
                    onChange={(next) => write([{ linkId: item.linkId, value: next }])}
                    requiredLabel={required}
                    readOnly={readOnly}
                    aria-label={label}
                  />
                ) : (
                  <PickListChip
                    options={row.options}
                    value={typeof v === 'string' ? v : null}
                    onChange={(next) => write([{ linkId: item.linkId, value: next }])}
                    requiredLabel={required}
                    readOnly={readOnly}
                    aria-label={label}
                  />
                )
              ) : row.multiple ? (
                <ChipChoice
                  multiple
                  options={row.options}
                  value={Array.isArray(v) ? v : []}
                  onChange={(next) => write([{ linkId: item.linkId, value: next }])}
                  requiredLabel={required}
                  readOnly={readOnly}
                  aria-label={label}
                />
              ) : (
                <ChipChoice
                  options={row.options}
                  value={typeof v === 'string' ? v : null}
                  onChange={(next) => write([{ linkId: item.linkId, value: next }])}
                  requiredLabel={required}
                  readOnly={readOnly}
                  aria-label={label}
                />
              )}
              {detail && (
                // G7: the same fact's text item, beside its pick (K6 detail; its own linkId and answer).
                <span id={noteItemAnchor(detail.linkId)} className="contents">
                  <NoteDetailText
                    label={label}
                    value={typeof detailValue === 'string' ? detailValue : ''}
                    readOnly={readOnly}
                    onChange={(text) => write([{ linkId: detail.linkId, value: text }])}
                  />
                </span>
              )}
            </Row>
          );
        }
        if (row.kind === 'line') {
          const v = values[item.linkId];
          return (
            <div key={item.linkId} className="flex flex-col gap-2">
              {/* L10: a text item with no value shows its label and a quiet "Add", never an empty box. */}
              <StateTwinContext.Provider value={isStateTwin(item.linkId, lineIds)}>
                <NoteTextLine
                  id={anchor}
                  label={label}
                  value={typeof v === 'string' ? v : ''}
                  onChange={(text) => (commands ? commands.writeText(item.linkId, text) : write([{ linkId: item.linkId, value: text }]))}
                  readOnly={readOnly}
                  commands={commands?.commandsFor(item.linkId)}
                />
              </StateTwinContext.Provider>
              {!readOnly && commands?.fillFor(item.linkId)}
            </div>
          );
        }
        if (row.kind === 'number') {
          return <NumberRow key={item.linkId} item={item} anchor={anchor} label={label} stored={values[item.linkId]} readOnly={readOnly} write={write} />;
        }
        if (row.kind === 'yes-no') {
          return (
            <Row key={item.linkId} id={anchor} label={label} align="start" stateTwin={isStateTwin(item.linkId, lineIds)}>
              <ChipChoice
                options={YES_NO_OPTIONS}
                value={yesNoValue(values[item.linkId])}
                onChange={(next) => write([{ linkId: item.linkId, value: next === 'true' }])}
                requiredLabel={item.required ? label : undefined}
                readOnly={readOnly}
                aria-label={label}
              />
            </Row>
          );
        }
        return <BlankRow key={item.linkId} item={item} anchor={anchor} label={label} stored={values[item.linkId]} readOnly={readOnly} write={write} />;
      })}
    </>
  );
}

/**
 * An optional number: the DS TextField, compact, with a numeric keyboard and the item's unit after it,
 * on the note line (desk, 2 Oct). The draft is local until the field is left or Enter is pressed; the
 * answer is then the parsed number, or nothing when the field was emptied.
 */
function NumberRow({
  item,
  anchor,
  label,
  stored,
  readOnly,
  write,
}: {
  item: QItem;
  anchor: string;
  label: string;
  stored: unknown;
  readOnly: boolean;
  write: StepBlockProps['write'];
}) {
  const shown = typeof stored === 'number' ? String(stored) : '';
  const [draft, setDraft] = useState<string | null>(null);
  // L10 (structure first): with no value, the line shows its label and a quiet "Add", never an empty box;
  // "Add" opens the field, focused, and leaving it empty closes it again. Never after the signature.
  const signed = useNoteSigned();
  const [opened, setOpened] = useState(false);
  const focusOnOpen = useRef(false);
  useEffect(() => {
    if (!opened || !focusOnOpen.current) return;
    focusOnOpen.current = false;
    document.getElementById(anchor)?.querySelector('input')?.focus();
  }, [opened, anchor]);
  const unit = item.unit ?? '';
  const whole = item.type === 'integer';
  // The WHOLE text is the number (Codex P1, 2 Oct: parseInt saved "1.5" as 1 and "1e2" as 1). A count
  // must be a whole number of 0 or more (audit FV2-09, 6 Oct: "-1" was kept as an impossible clinical
  // value); anything else is never written — the recorded answer stays — and the field says why and
  // what to type instead.
  const parse = (text: string): number | undefined => {
    const n = Number(text);
    return Number.isFinite(n) && (!whole || (Number.isSafeInteger(n) && n >= 0)) ? n : undefined;
  };
  const invalid = draft !== null && draft.trim() !== '' && parse(draft) === undefined;
  // COMMIT ON LEAVE (Codex P1, 2 Oct): typing "1.5" over a recorded 2 passes through "1" and "1.";
  // writing on each keystroke saved those prefixes. The draft stays local until the field is left (or
  // Enter): empty clears the answer, a valid number replaces it, anything else keeps the recorded
  // answer and keeps the draft and its message on screen until it is fixed.
  const commit = () => {
    if (draft === null) return;
    if (draft.trim() === '') {
      if (stored !== undefined) write([{ linkId: item.linkId, value: undefined }]);
      setDraft(null);
      return;
    }
    const n = parse(draft);
    if (n === undefined) return;
    if (n !== stored) write([{ linkId: item.linkId, value: n }]);
    setDraft(null);
  };
  if (!shown && draft === null && !opened) {
    if (readOnly || signed) return null;
    return (
      <Row id={anchor} label={label}>
        <AddEntryButton
          aria-label={`Add ${label}`}
          onClick={() => {
            focusOnOpen.current = true;
            setOpened(true);
          }}
        >
          Add
        </AddEntryButton>
      </Row>
    );
  }
  return (
    <Row id={anchor} label={label}>
      <TextField
        size="compact"
        // A TEXT input with a numeric keyboard (Codex P1, 2 Oct): a native number input reports an
        // unfinished draft such as "2e" as an empty value, which would read as "clear" and delete the
        // recorded answer. With text, the raw draft is always what is validated.
        type="text"
        inputMode={whole ? 'numeric' : 'decimal'}
        error={invalid ? (whole ? COUNT_INVALID : NUMBER_INVALID) : undefined}
        aria-label={unit ? `${label} (${unit})` : label}
        value={draft ?? shown}
        readOnly={readOnly}
        // FV2-12 / desk rule 6 Oct (signal-ds 2.20): a read-only answer is a fact, not a control — the value as
        // text with its unit, or "Not asked" in text-secondary; no box.
        readOnlyDisplay="text"
        {...(unit ? { readOnlyUnit: unit } : {})}
        containerClassName="w-28"
        onFocus={() => setDraft((d) => d ?? shown)}
        onBlur={() => {
          const leftEmpty = !(draft ?? shown).trim();
          commit();
          if (leftEmpty) setOpened(false);
        }}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter') commit();
        }}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDraft(e.target.value)}
      />
      {unit && !readOnly && <span className="font-sans text-sm text-text-secondary">{unit}</span>}
    </Row>
  );
}

/**
 * A required number (BlankChip). Filled, the chip shows the value and has no edit-in-place, so
 * "Change" opens a fresh blank BESIDE the recorded value: the answer stays saved until a new number
 * is committed, and "Keep" closes the blank with nothing changed (Codex P1, 2 Oct — Change used to
 * delete the answer first). `changing` is view state only, never a copy of the answer.
 */
function BlankRow({
  item,
  anchor,
  label,
  stored,
  readOnly,
  write,
}: {
  item: QItem;
  anchor: string;
  label: string;
  stored: unknown;
  readOnly: boolean;
  write: StepBlockProps['write'];
}) {
  const [changing, setChanging] = useState(false);
  const num =
    typeof stored === 'number' ? stored : typeof stored === 'string' && stored.trim() !== '' && Number.isFinite(Number(stored)) ? Number(stored) : null;
  const commit = (n: number) => {
    write([{ linkId: item.linkId, value: item.type === 'integer' ? Math.trunc(n) : n }]);
    setChanging(false);
  };
  const unit = item.unit ?? '';
  return (
    <Row id={anchor} label={label}>
      {changing && num !== null ? (
        <>
          <span className="text-text-secondary">
            Recorded {num}
            {unit ? ` ${unit}` : ''}
          </span>
          <BlankChip id={`${anchor}-blank`} label={label} unit={unit} value={null} onCommit={commit} />
          <Button variant="tertiary" size="sm" onClick={() => setChanging(false)}>
            Keep {num}
          </Button>
        </>
      ) : (
        <>
          <BlankChip id={`${anchor}-blank`} label={label} unit={unit} value={num} onCommit={commit} readOnly={readOnly} />
          {num !== null && !readOnly && (
            <Button variant="tertiary" size="sm" onClick={() => setChanging(true)} aria-label={`Change ${label}`}>
              Change
            </Button>
          )}
        </>
      )}
    </Row>
  );
}

/**
 * Items the chips do not draw, drawn by the DS renderer scoped to exactly `group` (the authored group,
 * pruned), merged into the ONE response by linkId — the same merge every renderer emit uses. A
 * computed value the renderer adds is dropped: a score is never saved as an answer (UC-29f).
 */
function FieldRun({
  group,
  response,
  sharedResponseRef,
  onResponseChange,
  role,
  label,
  readOnly,
}: {
  group: QItem;
  response: QuestionnaireResponse;
  sharedResponseRef: MutableRefObject<QuestionnaireResponse>;
  onResponseChange: (r: QuestionnaireResponse) => void;
  role: ShellRole;
  label: string;
  readOnly: boolean;
}) {
  const scope = useMemo<Questionnaire>(() => ({ resourceType: 'Questionnaire', item: [group] }), [group]);
  const calcIds = useMemo(() => calculatedIds([group]), [group]);
  const onChange = useCallback(
    (emitted: QuestionnaireResponse) => {
      const merged = mergeSectionResponse(sharedResponseRef.current, scope, emitted);
      const next = calcIds.size ? { ...merged, answers: merged.answers.filter((a) => !calcIds.has(a.linkId)) } : merged;
      sharedResponseRef.current = next;
      onResponseChange(next);
    },
    [sharedResponseRef, scope, calcIds, onResponseChange],
  );
  return (
    <QuestionnaireRenderer
      questionnaire={scope}
      aria-label={label}
      response={response}
      onChange={onChange}
      unwrapPageGroup
      mode={readOnly ? 'review' : 'capture'}
      buttonCursor="pointer"
      role={role === 'nurse' ? 'nurse' : 'physician'}
    />
  );
}

/** A stable per-group FieldRun factory for a body. */
function useFieldRun(p: StepBlockProps, label: string, readOnly: boolean) {
  return useCallback(
    (group: QItem) => (
      <FieldRun
        group={group}
        response={p.response}
        sharedResponseRef={p.sharedResponseRef}
        onResponseChange={p.onResponseChange}
        role={p.role}
        label={group.text ?? label}
        readOnly={readOnly}
      />
    ),
    [p.response, p.sharedResponseRef, p.onResponseChange, p.role, label, readOnly],
  );
}

/**
 * `.` phrases and `/` insert inside every NoteFieldLine of a step (desk D-61 ruling C), and the fill
 * panel a piece phrase opens (PROD-2824 · TS-5). Every line of the step takes the SAME library:
 *   · offered where it is targeted (`lineEditors`): the step's own narrative editor; on HPI also the
 *     library's chief-complaint note; for a nurse also her nursing note;
 *   · narrative rows only, and only rows the author may issue (`lineCommandItems`): "/" never offers an
 *     order row to a nurse (or anyone: a note line holds narrative);
 *   · a phrase WITHOUT pieces inserts its text at the field's cursor (NoteFieldLine's own insert);
 *   · a phrase WITH pieces (`?ef=phrasePieces` only) removes the typed token and opens the fill panel
 *     under THAT line; "Insert phrase" adds the finished prose at the end of the field (after a trailing
 *     space as typed, else on a new line — `insertAt`, the block Macros' rule).
 * No library → no commands, and the field's hint never advertises a menu it cannot open.
 */
function useLineCommands(
  p: StepBlockProps,
  step: PaStepKey,
  author: AuthorRole,
  readOnly: boolean,
): LineCommands & { openFill: (linkId: string, macro: NoteMacro) => void } {
  const { phrases, write, liveValues, noteDeltasLive, onNoteDeltaChange } = p;
  /** PROD-2961: the line's pieces as they stand NOW (the live Delta against the live text), if it holds any. */
  const liveSegs = (linkId: string): LineSeg[] | undefined =>
    onNoteDeltaChange ? lineSegs(noteDeltasLive?.current[linkId], liveValues()[linkId]) : undefined;
  /** Write a line: its Delta first (pieces kept, or dropped when none is left), then its text. */
  const writeSegs = (linkId: string, segs: LineSeg[]) => {
    onNoteDeltaChange?.(linkId, segsHavePieces(segs) ? segsToDelta(segs) : undefined);
    write([{ linkId, value: segsText(segs) }]);
  };
  // PROD-2961: a plain edit in the field keeps every piece it does not touch (`rebaseSegs`); a piece the
  // edit reaches becomes text (the checksum rule). A line with no pieces writes exactly as before.
  const writeText = (linkId: string, text: string) => {
    const segs = liveSegs(linkId);
    if (segs) {
      writeSegs(linkId, rebaseSegs(segs, text));
      return;
    }
    // A stored Delta that no longer derives the text (another writer changed it) is dropped for good at
    // the first plain edit — it must not come back if the text happens to read the same again (Codex P1).
    // Unconditional (Codex P2, round 2): the live copy may lag; clearing an absent key changes nothing.
    onNoteDeltaChange?.(linkId, undefined);
    write([{ linkId, value: text }]);
  };
  const [draft, setDraft] = useState<PhraseFillDraft | null>(null);
  // A line that freezes (the note signed) drops any open fill — adjusted during render, never an effect.
  if (readOnly && draft) setDraft(null);
  // `anchor`: where `.` was typed in the line (PROD-3148); the block's Macros menu passes none, so its phrase is appended.
  const openFill = useCallback(
    (linkId: string, macro: NoteMacro, anchor?: TriggerAnchor) =>
      setDraft(startPhraseFill(linkId, macro, (q) => resolveChart(q, phrases?.chartReads), anchor)),
    [phrases],
  );
  const offered = useMemo(() => {
    if (!phrases) return [];
    const editors = lineEditors(step, author, phrases?.discipline);
    // G8 (Part 2): the complaint's starter-map phrases first, in their order; every other phrase after them.
    return suggestedFirst(offeredPhrasesIn(phrases.macros, editors, { pieces: !!phrases.pieces }), phrases.suggestFor?.(editors) ?? []);
  }, [phrases, step, author]);
  const { insertableTemplates, onInsertTemplate } = p;
  // "/" adds a template as its own answer set: only the questionnaire steps' lines offer it, and only with a host to add it.
  const templateRows = useMemo(
    () => (STRUCTURED.has(step) && onInsertTemplate && insertableTemplates?.length ? toTemplateCommandItems(insertableTemplates) : []),
    [step, insertableTemplates, onInsertTemplate],
  );
  const items = useMemo(() => lineCommandItems([...toCommandItems(offered), ...templateRows], author), [offered, templateRows, author]);
  const commandsFor = (linkId: string): NoteFieldCommands | undefined =>
    (phrases || templateRows.length > 0) && !readOnly
      ? {
          items,
          role: author,
          onInsert: (item: CommandMenuItem) => {
            // A template pick starts its own answer set; the "/" token is removed and nothing is written into the line.
            const templateId = templateRowQuestionnaireId(item);
            if (templateId) {
              onInsertTemplate?.(templateId);
              return '';
            }
            const macro = offered.find((m) => m.id === item.id);
            if (!macro) return undefined;
            if (carriesPieces(macro)) {
              // Read where the `.` token starts now: NoteFieldLine removes it after this returns (PROD-3148).
              openFill(linkId, macro, triggerAnchorIn(noteItemAnchor(linkId)));
              return undefined;
            }
            return phrasePlainText(macro);
          },
        }
      : undefined;
  // Chart links still unresolved are re-read from the page's latest reads on every render (Codex P2):
  // the preview and the inserted prose quote the chart once it has loaded.
  const liveDraft = draft ? { ...draft, ops: withFreshCharts(draft.ops, (q) => resolveChart(q, phrases?.chartReads)) } : null;
  // After Complete or Remove, keyboard focus goes back to the line the phrase was called from.
  const backToLine = (linkId: string) => document.getElementById(noteItemAnchor(linkId))?.focus();
  const fillFor = (linkId: string): ReactNode =>
    !readOnly && liveDraft && liveDraft.linkId === linkId ? (
      <PhraseOccurrence
        // A new phrase is a new occurrence: its parts, its focus and its Refresh notes start fresh.
        key={liveDraft.key}
        draft={liveDraft}
        onChange={(ops) => setDraft({ ...liveDraft, ops })}
        onRemove={() => {
          setDraft(null);
          backToLine(linkId);
        }}
        onRefreshChart={(index) => {
          const next = withRefreshedChart(liveDraft.ops, index, (q) => resolveChart(q, phrases?.chartReads));
          setDraft({ ...liveDraft, ops: next.ops });
          return { changed: next.changed, pulledAt: next.pulledAt };
        }}
        renderChartPiece={phrases?.renderChartPiece}
        onComplete={(text) => {
          const v = liveValues()[linkId];
          const current = typeof v === 'string' ? v : '';
          // PROD-2961: with the line Deltas wired, the phrase lands WITH its pieces as completed (chosen codes,
          // blank values, chart snapshots with their source ids); its words are exactly `text`. Without
          // them, as prose only (PROD-2824).
          // PROD-3148: where `.` was typed, found again in the line as it is now — mid-line or at its end, on the same line
          // in both paths below (PROD-3166). Appended on its own line only when the phrase came from the block's Macros
          // menu, or when the line changed on both sides of that place.
          const segs = liveSegs(linkId) ?? plainSegs(current);
          const at = liveDraft.anchor ? placeAt(liveDraft.anchor, segsText(segs)) : undefined;
          const pad = liveDraft.anchor?.pad;
          if (onNoteDeltaChange) writeSegs(linkId, insertPhrase(segs, liveDraft.ops, at, pad));
          else if (at !== undefined && at <= current.length) write([{ linkId, value: spliceAt(current, at, text, pad) }]);
          else write([{ linkId, value: insertAt(current, current.length, text).text }]);
          setDraft(null);
          backToLine(linkId);
        }}
      />
    ) : null;
  return { commandsFor, fillFor, openFill, writeText };
}

/** The linkIds a body's narrative must not draw (the section's phrase narrative), stable per value. */
function useSkip(linkId: string | undefined): ReadonlySet<string> {
  return useMemo(() => new Set(linkId ? [linkId] : []), [linkId]);
}

// ── HPI ──────────────────────────────────────────────────────────────────────────────────────

function HpiBody(p: StepBlockProps & { sectionQ: Questionnaire; narrative: NarrativeGroup[]; fieldCommands?: LineCommands }) {
  const { sectionQ: section, values, write } = p;
  // HPI is editable until the provider signs (PROD-2810 / rule 38); after that only while amending.
  // entry the OFF renderer keeps (encounter-shell-view.tsx, `mode`). Only ROS and PE freeze at sign.
  const readOnly = p.answersFrozen;
  const marker = useMemo(() => findChiefComplaintHpiMarker(section.item), [section]);
  // D-74 (design desk, 6 Oct): exactly one Vitals block, and it lives in Objective — an HPI vitals-block marker
  // is not drawn in frame v2 (objective-vitals.tsx draws the block first in Objective instead).
  // D-72 amendment: the Associated line's ROS group — the present ROS findings for the complaint's systems
  // (complaint map v2, the same keys that order the ROS) — and its "Add in ROS" jump.
  const rosSystems = useMemo(() => {
    const ros = p.questionnaire && p.navigableSteps.includes('ros') ? questionnaireForStep(p.questionnaire, 'ros') : undefined;
    return ros && ros.item.length > 0 ? rosModel(withSymptomChipDefaults(ros, 'ros'), values).systems : undefined;
  }, [p.questionnaire, p.navigableSteps, values]);
  const rosFindingsFor = (complaint: { display: string }): string[] => {
    if (!rosSystems) return [];
    const display = complaint.display.trim().toLowerCase();
    const ref = p.chiefComplaints?.find((c) => (c.display ?? '').trim().toLowerCase() === display) ?? { display: complaint.display };
    const keys = systemOrderFor('ros', [ref]) ?? [];
    return rosSystems
      .filter((sys) => keys.some((k) => systemMatchesKey(sys.code, k)))
      .flatMap((sys) => sys.items.filter((it) => values[it.linkId] === true).map((it) => it.text));
  };
  const onAddInRos = rosSystems
    ? () => {
        const focusRos = () => {
          const block = document.getElementById('note-block-ros');
          if (!block) return false;
          scrollJumpTarget(block);
          const heading = block.querySelector<HTMLElement>('h2, h3') ?? block;
          if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
          heading.focus({ preventScroll: true });
          return true;
        };
        // ROS is in S beside HPI; when the note shows one section at a time and ROS is not on screen, go there first.
        if (!focusRos()) {
          p.onStepChange('ros');
          setTimeout(focusRos, 0);
        }
      }
    : undefined;
  return (
    <div className="flex flex-col gap-3" data-structured-body="hpi">
      {marker && p.resolveChiefComplaint && (
        /* The anchor the long page's "Chief complaint" cell in the section bar jumps to (19 Oct): the chief complaint is part of HPI, so
           it is not its own section; the cell scrolls to this block. */
        <div data-note-anchor="chief-complaint">
          <ChiefComplaint
            marker={marker}
            resolve={p.resolveChiefComplaint}
            frozen={p.recordsFrozen}
            sex={p.patientSex}
            rosFindingsFor={rosFindingsFor}
            onAddInRos={onAddInRos}
            role={p.author}
          />
        </div>
      )}
      <Narrative groups={p.narrative} values={values} readOnly={readOnly} write={write} commands={p.fieldCommands} repeats={titleRepeats(p)} />
    </div>
  );
}

/** The titles a template group must not repeat inside a block: the S·O·A·P section's and the block's own
 *  (frame v2 integration, each title once — design desk 5 Oct, audit FV2-04). A QuestionBank pack names a
 *  step's top group after its SOAP letter ("Subjective", "Objective"), which the note already says above it. */
function titleRepeats(p: Pick<StepBlockProps, 'section' | 'step' | 'stepTitles' | 'blockTitle'>): readonly string[] {
  return [p.section.title, p.blockTitle ?? noteBlockLabel(p.step, p.stepTitles)];
}

const sameTitle = (a: string, b: string) => a.replace(/\s+/g, ' ').trim().toLowerCase() === b.replace(/\s+/g, ' ').trim().toLowerCase();

/** The step's note lines inside its block, by authored group (`stepNarrative`'s `inBlock`). A group whose
 *  title repeats the section's or the block's (`repeats`) draws its lines without that title. */
function Narrative({
  groups,
  values,
  readOnly,
  write,
  commands,
  repeats = [],
  pickList = false,
}: {
  groups: readonly NarrativeGroup[];
  values: AnswerMap;
  readOnly: boolean;
  write: StepBlockProps['write'];
  commands?: LineCommands;
  repeats?: readonly string[];
  /** Risk step: K1/K2 closed pick lists for `choice` items (see `RowsProps.pickList`). */
  pickList?: boolean;
}) {
  return (
    <>
      {groups.map((g) => (
        // Only a heading on screen is a title a line label must not repeat: a heading left out because it repeats the
        // block's title (`repeats`) hides nothing else, so the line keeps its words (shownGroupTitle).
        <NoteGroupTitleContext.Provider key={g.linkId} value={shownGroupTitle(g.title, g.untitled, repeats)}>
        <div className="flex flex-col gap-1" data-note-group={g.linkId}>
          {shownGroupTitle(g.title, g.untitled, repeats) && <SectionEyebrow as="h3" className={GROUP_TITLE_WRAP}>{g.title}</SectionEyebrow>}
          <NarrativeRows rows={g.rows} values={values} readOnly={readOnly} write={write} commands={commands} pickList={pickList} />

        </div>
        </NoteGroupTitleContext.Provider>
      ))}
    </>
  );
}

/**
 * The chief complaint and its attributes as note lines (PROD-3042, design desk D-72 as amended 6 Oct):
 * one always-visible line per attribute group, each a closed pick list — no "Show details" fold and no
 * chip matrix (complaint-lines.tsx). With no complaint yet, the DS block shows its picker only. The write
 * is the page's, unchanged.
 */
function ChiefComplaint({
  marker,
  resolve,
  frozen,
  sex,
  rosFindingsFor,
  onAddInRos,
  role,
}: {
  role: AuthorRole;
  marker: QItem;
  resolve: NonNullable<QuestionnaireRendererProps['resolveChiefComplaint']>;
  frozen: boolean;
  sex?: string | null;
  rosFindingsFor?: (complaint: { display: string }) => readonly string[];
  onAddInRos?: () => void;
}) {
  const resolved = resolve(marker);
  // Frozen by a provider sign (the complaint lives in the encounter record): every write handler is
  // dropped, so the lines read as text with no picker, no remove, no Clear and no comment.
  const block =
    resolved && frozen
      ? { ...resolved, onAdd: undefined, onSearch: undefined, onRemove: undefined, onToggle: undefined, onClearCategory: undefined, onSaveComment: undefined }
      : resolved;
  if (!block) return null;
  return (
    <ComplaintLines
      block={block}
      frozen={frozen}
      sex={sex}
      rosFindingsFor={rosFindingsFor}
      onAddInRos={onAddInRos}
      role={role}
    />
  );
}

// ── Risk ─────────────────────────────────────────────────────────────────────────────────────

/** Risk's items that map onto the note: its authored chips (SymptomChips), yes/no and coded
 *  questions, free text. Editable until the provider signs; after that only while amending (rule 38). */
function RiskBody(p: StepBlockProps & { narrative: NarrativeGroup[]; fieldCommands?: LineCommands; riskSystems: Record<string, RosSystem> }) {
  return (
    <div className="flex flex-col gap-3" data-structured-body="risk">
      {p.narrative.map((g) =>
        p.riskSystems[g.linkId] ? (
          <RiskSystemRow key={g.linkId} {...p} system={p.riskSystems[g.linkId]} />
        ) : (
          <Narrative key={g.linkId} groups={[g]} values={p.values} readOnly={p.answersFrozen} write={p.write} commands={p.fieldCommands} repeats={titleRepeats(p)} pickList />
        ),
      )}
    </div>
  );
}

/**
 * PROD-3042 (F7): Fall and Pain as note lines (risk-record-lines.ts) — "Fall risk present" and "Pain present"
 * are yes/no pick lists, the details are the note's own lines. 7 Oct 2026 ruling (CDC STEADI and NRS 0–10 replace
 * Morse and Wong-Baker): the Morse questions, the pain scale and the Wong-Baker score are an old record's read-only
 * history (with the Morse total beside them), never asked on a new note. No switch, no "Done", no "Select…": each answer saves on change through
 * the one live response, which the page's Risk bridge forwards to EncounterMetaData as it did for the cards.
 * Held back until the saved assessment has merged (`riskReady`), and read-only after the provider sign,
 * amending or not (rule 38).
 */
function RiskRecords(p: StepBlockProps & { recordQ: Questionnaire }) {
  const { recordQ, values, recordsFrozen, riskReady = true, riskReadFailed } = p;
  // `?x=nurse-encounter-v3` (PROD-3404, ON on the dev hosts): Fall and Pain as the catalog's optional block. Read once per mount.
  const [optionalOn] = useState(() => isEncounterFlagOn('nurseEncounterV3'));
  const items = useMemo(() => questionnaireForStep(recordQ, 'risk').item, [recordQ]);
  // `signalNurseEncounter`: the nurse's Pain asks Present, NRS 0-10 and the location only (risk-record-lines.ts).
  const nursePain = useMemo(() => isSignalNurseEncounter(p.author), [p.author]);
  const groups = useMemo(() => riskRecordGroups(items, values, p.riskSteadi, nursePain), [items, values, p.riskSteadi, nursePain]);
  if (!riskReady) return null;
  const failed = riskReadFailed && (riskReadFailed.fall || riskReadFailed.pain) ? riskReadFailed : undefined;
  // PROD-3142 (D-78): a list refused as stale is frozen like a signed record, until a reload; the other keeps saving.
  const frozen = (g: RiskRecordGroup) => recordsFrozen || !!p.riskFrozen?.[g.kind];
  const writeFor = (g: RiskRecordGroup) => (updates: readonly AnswerUpdate[]) => {
    if (frozen(g)) return;
    p.write(riskRecordWrites(g, p.liveValues(), updates));
  };
  const line = (g: RiskRecordGroup, row: RiskRecordRow) => riskRecordLine(p, g, row, frozen, writeFor);
  return (
    <div className="flex flex-col gap-3" data-risk-records>
      {failed && (
        <Banner
          variant="warning"
          title={`The saved ${failed.fall && failed.pain ? 'fall and pain assessments' : failed.fall ? 'fall assessment' : 'pain assessment'} could not be loaded`}
        >
          {`Changes to ${failed.fall && failed.pain ? 'them' : 'it'} will not be saved, so the stored record is not overwritten. Reload the encounter to try again.`}
        </Banner>
      )}
      {groups.map((g) => {
        const head = g.rows[0];
        if (optionalOn && head?.kind === 'yes-no') {
          // PROD-3404 (`?x=nurse-encounter-v3`): the catalog's optional block (risk-optional-block.tsx). Same item, same write.
          const rest = g.rows.slice(1);
          const stored = values[head.item.linkId];
          const value = stored === true ? true : stored === false ? false : undefined;
          const lines = rest.map((row) => line(g, row));
          return (
            <div key={g.group.linkId} data-risk-record={g.kind}>
              <RiskOptionalBlock
                group={g.group}
                present={head.item}
                value={value}
                readOnly={frozen(g)}
                onPresentChange={(yes) => writeFor(g)([{ linkId: head.item.linkId, value: yes }])}
                answered={riskAnsweredLines(rest, values)}
                kept={value !== true && rest.length > 0 ? lines : undefined}
              >
                {lines}
              </RiskOptionalBlock>
            </div>
          );
        }
        return (
          <div key={g.group.linkId} className="flex flex-col gap-1" data-risk-record={g.kind}>
            {g.group.text && !titleRepeats(p).some((r) => sameTitle(r, g.group.text as string)) && <SectionEyebrow as="h3" className={GROUP_TITLE_WRAP}>{g.group.text}</SectionEyebrow>}
            {g.rows.map((row) => line(g, row))}
          </div>
        );
      })}
    </div>
  );
}

/** PROD-3404: "label: value" of each answered line, for the optional block's folded summary. A `fields` run (the DS fields a
 *  line has no note line for) reads its own answered items, so its answers are summarised too. */
function riskAnsweredLines(rows: readonly RiskRecordRow[], values: AnswerMap): string[] {
  const out: string[] = [];
  // The label without its bracketed note ("History of falling (acute fall within 3-6 months)" → "History of falling").
  const labelOf = (item: QItem) => (item.text ?? item.linkId).replace(/\s*\(.*$/, '');
  // An answer as its line shows it: a yes / no as the chip's label, a coded pick as its option's label (the stored value is the
  // option's value, `pickOptions`), a coding as its display, a date in the house format (D-45). Never "true" or "[object Object]".
  const shown = (item: QItem, v: unknown): string => {
    if (typeof v === 'boolean') return YES_NO_OPTIONS.find((o) => o.value === String(v))?.label ?? '';
    if (v && typeof v === 'object' && !(v instanceof Date)) {
      const c = v as { display?: string; code?: string; valueCoding?: { display?: string; code?: string } };
      return c.display ?? c.valueCoding?.display ?? c.code ?? c.valueCoding?.code ?? '';
    }
    const date = toNoteDate(v);
    if (date) return formatPickerDate(date, NOTE_DATE_FORMAT) ?? String(v);
    return pickOptions(item).find((o) => o.value === String(v))?.label ?? String(v);
  };
  const answered = (item: QItem) => {
    const v = values[item.linkId];
    if (!isAnswered(v)) return;
    const text = (Array.isArray(v) ? v.map((x) => shown(item, x)) : [shown(item, v)]).filter(Boolean).join(', ');
    if (text) out.push(`${labelOf(item)}: ${text}`);
  };
  // A `fields` run's items, nested groups included (their answers are summarised too).
  const walk = (items: readonly QItem[] | undefined) => {
    for (const item of items ?? []) {
      if (item.type === 'group') walk(item.item);
      else answered(item);
    }
  };
  for (const row of rows) {
    if (row.kind === 'heading') continue;
    if (row.kind === 'fields') {
      walk(row.group.item);
      continue;
    }
    if (!('item' in row)) continue;
    if (row.kind === 'history' || row.kind === 'total') {
      out.push(`${labelOf(row.item)}: ${row.text}`);
      continue;
    }
    answered(row.item);
  }
  return out;
}

/** One Fall / Pain line, as RiskRecords draws it (the optional block draws the same lines). */
function riskRecordLine(
  p: StepBlockProps,
  g: RiskRecordGroup,
  row: RiskRecordRow,
  frozen: (g: RiskRecordGroup) => boolean,
  writeFor: (g: RiskRecordGroup) => (updates: readonly AnswerUpdate[]) => void,
): ReactNode {
  const { values } = p;
            if (row.kind === 'heading') {
              // PROD-3128: the STEADI follow-up's heading, drawn as the template's own group titles are.
              return (
                <SectionEyebrow key={row.item.linkId} as="h3" className={`${GROUP_TITLE_WRAP} mt-2`} data-part="risk-steadi-heading">
                  {row.text}
                </SectionEyebrow>
              );
            }
            if (row.kind === 'total') {
              return (
                <Row key={row.item.linkId} id={noteItemAnchor(row.item.linkId)} label={row.item.text ?? row.item.linkId}>
                  <span className="font-semibold" data-part="risk-total">
                    {row.text}
                  </span>
                </Row>
              );
            }
            if (row.kind === 'history') {
              // 7 Oct 2026 ruling: a Morse / Wong-Baker answer of an old record — read-only history, never asked or written.
              return (
                <Row key={row.item.linkId} id={noteItemAnchor(row.item.linkId)} label={row.item.text ?? row.item.linkId}>
                  <span className="text-size-sm text-text-primary" data-part="risk-history">
                    {row.text}
                  </span>
                </Row>
              );
            }
            if (row.kind === 'date') {
              // K7 (the 6 Oct contract): "Add" opens the DS date picker; a set date reads as text with "Edit". The write
              // goes through the card's `withPresent` too, like every Fall / Pain line.
              return (
                <NoteDateLine
                  key={row.item.linkId}
                  id={noteItemAnchor(row.item.linkId)}
                  label={row.item.text ?? row.item.linkId}
                  value={values[row.item.linkId]}
                  readOnly={frozen(g) || row.item.readOnly === true}
                  notAfterToday
                  onChange={(d) => writeFor(g)([{ linkId: row.item.linkId, value: d }])}
                />
              );
            }
            if (row.kind === 'fields') {
              // A date keeps the DS field; its change goes through the card's `withPresent` too (Codex review).
              return (
                <div key={`fields:${row.group.item?.[0]?.linkId ?? row.group.linkId}`} data-outside-items={(row.group.item ?? []).map((i) => i.linkId).join(' ')}>
                  <FieldRun
                    group={row.group}
                    response={p.response}
                    sharedResponseRef={p.sharedResponseRef}
                    role={p.role}
                    label={g.group.text ?? 'Risk'}
                    readOnly={frozen(g)}
                    onResponseChange={(r) => {
                      const pre = presentPrelude(g, answerMap(r));
                      const next = pre.length > 0 ? applyUpdates(r, pre) : r;
                      p.sharedResponseRef.current = next;
                      p.onResponseChange(next);
                    }}
                  />
                </div>
              );
            }
            return <NarrativeRows key={row.item.linkId} rows={[row]} values={values} readOnly={frozen(g)} write={writeFor(g)} pickList />;
}

/**
 * One chip-only Risk group on the ROS system row (desk, 2 Oct): ReviewOfSystemsBlock with no system
 * picker and no bulk of its own — the Risk block's bulk is its header All Neg (#559's
 * fillSectionNegative, G7). A risk group (suicide, self-harm, harm to others) gets no Clear either:
 * the block's own `RosSystem.bulk` default (signal-ds #444). Read-only once frozen by a provider sign (rule 38).
 */
function RiskSystemRow(p: StepBlockProps & { system: RosSystem }) {
  const { system, values, write, liveValues, answersFrozen } = p;
  // Frozen: no row bulk either (its "Clear" is a Risk clear like the header's; Andrew's review, 6 Oct).
  const systems = useMemo(() => [answersFrozen ? { ...system, bulk: false } : system], [system, answersFrozen]);
  const findings = useMemo(() => rosFindings(systems, values), [systems, values]);
  return (
    <div data-risk-system={system.code}>
      <ReviewOfSystemsBlock
        systems={systems}
        selected={[]}
        onSelectedChange={() => {}}
        systemPicker={false}
        bulk={false}
        allowedStates={ROS_STORABLE}
        findings={findings}
        readOnly={answersFrozen}
        {...FINDING_BLOCK}
        onFindingsChange={(next: RosFindings, origin: ChangeOrigin) => {
          if (answersFrozen) return;
          write(rosChanges(systems, liveValues(), findings, next, origin).updates);
        }}
      />
    </div>
  );
}

// ── ROS ──────────────────────────────────────────────────────────────────────────────────────

function RosBody(p: StepBlockProps & { sectionQ: Questionnaire; narrative: NarrativeGroup[]; fieldCommands?: LineCommands }) {
  const { sectionQ: section, values, write, answersFrozen, chiefComplaints } = p;
  const readOnly = answersFrozen;
  const chipped = useMemo(() => withSymptomChipDefaults(section, 'ros'), [section]);
  const order = systemOrderFor('ros', chiefComplaints);
  // D-71 amendment: a group repeating the section or block title draws as its own groups.
  const repeatKey = titleRepeats(p).join('\u0000');
  const model = useMemo(() => rosModel(chipped, values, order, repeatKey.split('\u0000')), [chipped, values, order, repeatKey]);
  // K5 (amended 7 Oct): one line per system — every system drawn, the complaint's first (rosModel orders them), none
  // hidden; the same finding linkIds are written.
  // `signalNurseEncounter` (page 56): the nurse's ROS drops Stridor / Apnea / Choking and offers Absent / Present only.
  const nurseSignal = useMemo(() => isSignalNurseEncounter(p.author), [p.author]);
  const lines = useMemo(() => {
    const all = model.systems.map((s): SystemLine => ({
      code: s.code,
      display: s.display,
      findings: s.items.map((i) => ({ linkId: i.linkId, text: i.text, kind: 'finding' as const, risk: !!i.riskItem })),
    }));
    return nurseSignal ? nurseRosLines(all) : all;
  }, [model, nurseSignal]);
  return (
    <div className="flex flex-col gap-3" data-structured-body="ros" data-nurse-signal={nurseSignal ? 'on' : undefined}>
      {lines.length > 0 && <SystemLines kind="ros" lines={lines} values={values} readOnly={readOnly} write={write} nurseSignal={nurseSignal} />}
      <Narrative groups={p.narrative} values={values} readOnly={readOnly} write={write} commands={p.fieldCommands} repeats={titleRepeats(p)} />
    </div>
  );
}

// ── PE ───────────────────────────────────────────────────────────────────────────────────────

function ExamBody(p: StepBlockProps & { sectionQ: Questionnaire; narrative: NarrativeGroup[]; fieldCommands?: LineCommands }) {
  const { sectionQ: section, values, write, answersFrozen, chiefComplaints } = p;
  const readOnly = answersFrozen;
  const chipped = useMemo(() => withSymptomChipDefaults(section, 'pe'), [section]);
  const order = systemOrderFor('pe', chiefComplaints);
  // D-71 amendment: a group repeating the section or block title ("Objective" under Objective) draws as its
  // own groups (General appearance, Skin, …) — the template's own rows, the repeated heading not drawn again.
  const repeatKey = titleRepeats(p).join('\u0000');
  const model = useMemo(() => examModel(chipped, values, order, repeatKey.split('\u0000')), [chipped, values, order, repeatKey]);
  // K5 (amended 7 Oct): one line per system; an exam item is a +/- finding or a normal statement (examModel's kinds).
  const lines = useMemo(
    () =>
      model.systems.map((s): SystemLine => ({
        code: s.code,
        display: s.display,
        findings: (s.items ?? []).map((i) => ({ linkId: i.linkId, text: i.text, kind: i.kind === 'normal' ? ('normal' as const) : ('finding' as const), risk: !!i.riskItem })),
      })),
    [model],
  );
  // ONE ExamBlock for the exam (judge J3): one row per template group, its present/absent items as the
  // row's chips and sentence. No host "opened" notion exists yet, so "Normal on the rest" reaches only
  // systems already answered in (the block's default, `openedSystems` unset).
  const noExam = useMemo(() => isSignalNurseEncounter(p.author), [p.author]);
  return (
    <div className="flex flex-col gap-3" data-structured-body="pe" data-nurse-signal={noExam ? 'on' : undefined}>
      {/* D-74: exactly one Vitals block, at the FIRST vitals-block marker inside Objective, in every D-45 state — with no
          resolver it says "Vitals information unavailable" (integration, Codex P2: it vanished). */}
      {model.vitals[0] && (
        <VitalsRecordBody
          resolve={p.resolveVitals}
          marker={model.vitals[0]}
          state={p.vitalsStatus}
          frozen={p.recordsFrozen}
          titled
        />
      )}
      {/* `signalNurseEncounter` (page 56): no physical exam for the nurse; her Objective keeps the vitals only. */}
      {!noExam && lines.length > 0 && (
        // PROD-2789: every exam system is drawn, the complaint's first (examModel orders them); none hidden.
        <SystemLines kind="exam" lines={lines} values={values} readOnly={readOnly} write={write} />
      )}
      {!noExam && <Narrative groups={p.narrative} values={values} readOnly={readOnly} write={write} commands={p.fieldCommands} repeats={titleRepeats(p)} />}
    </div>
  );
}

// ── Free screens recorded in Flutter (7 Oct 2026 ruling) ───────────────────────────────────

/**
 * The managed lines Flutter writes into the Fall / Pain record for the free screens — "CDC STEADI key questions: …" and
 * "Pain NRS 0-10 (self-report): N" — read back as they are written, read-only. Drawn only when the template authors no
 * such screen anywhere in the note (otherwise the note's own lines hold it: one place per fact, G7).
 */
function ManagedRiskLines({ managed, items, followUp = false }: { managed: RiskManaged; items: readonly QItem[]; followUp?: boolean }) {
  const scope = freeScreenScope(items);
  const lines = [
    // PROD-3128: the STEADI follow-up's own lines hold the STEADI answers (G7), so its managed line is not drawn twice.
    ...(managed.steadi && Object.keys(scope.steadi).length === 0 && !followUp ? [managed.steadi] : []),
    ...(managed.nrs && !scope.nrs ? [managed.nrs] : []),
  ];
  if (lines.length === 0) return null;
  return (
    <div className="flex flex-col gap-1" data-part="risk-managed">
      {lines.map((l) => (
        <Row key={l.label} label={l.label}>
          <span className="text-size-sm text-text-primary">{l.value}</span>
        </Row>
      ))}
    </div>
  );
}

// ── K5: one line per system (ROS and exam) ──────────────────────────────────────────────────

/**
 * K5, amended 7 Oct (system-lines-model.ts): each system is ONE line — a K1 pick ("Denies / Reports / Not asked",
 * "Normal / Abnormal / Not examined"); "Reports" / "Abnormal" opens ONE K2 multi-pick of the system's findings (the
 * ChipChoice, 19 Oct: the findings as visible chips, the first 8 and "+N more"; was the DS PickListChip list). The answers
 * already recorded beside the picks read as text ("Denies vomiting, fever"). Read-only (signed), each system is its
 * text. "Denies for the rest" / "Normal on the rest" fills only the unanswered items of systems already answered in,
 * never a risk item (system-lines-model.ts).
 */
function SystemLines({ kind, lines, values, readOnly, write, nurseSignal = false }: { kind: SystemKind; lines: readonly SystemLine[]; values: AnswerMap; readOnly: boolean; write: (u: readonly AnswerUpdate[]) => void; nurseSignal?: boolean }) {
  // `signalNurseEncounter`: the nurse's ROS lines read Absent / Present (no "Not asked"); a second click clears, as on the exam.
  const nurseRos = nurseSignal && kind === 'ros';
  // "Reports" / "Abnormal" chosen before any finding is picked: VIEW state, so the findings pick shows. A system with a
  // finding recorded present is positive from its answers, open or not.
  const [opened, setOpened] = useState<readonly string[]>([]);
  // "Not asked" picked on purpose (22 Oct review): the derived state of an unanswered system is none, so without this VIEW
  // state the chip could never show pressed. An answer recorded later wins (the derived state is no longer none) and clears it.
  const [notAsked, setNotAsked] = useState<readonly string[]>([]);
  // The pick follows the data: once a remembered line gains a recorded answer from anywhere (another view, a macro, a
  // reset that refills it), it forgets "Not asked", so clearing that answer later does not bring back a stale pressed chip.
  // Adjusted while rendering when the answers change (React's "adjust state on a prop change"), never in an effect.
  const [notAskedSeen, setNotAskedSeen] = useState(values);
  if (values !== notAskedSeen) {
    setNotAskedSeen(values);
    const answered = notAsked.filter((code) => {
      const line = lines.find((l) => l.code === code);
      return !line || systemState(line, values) !== 'none';
    });
    if (answered.length > 0) setNotAsked((o) => o.filter((c) => !answered.includes(c)));
  }
  const rest = readOnly ? [] : restNegative(kind, lines, values);
  const restLabel = kind === 'ros' ? 'Denies for the rest' : 'Normal on the rest';
  return (
    <div className="flex flex-col gap-1" data-system-lines={kind}>
      {rest.length > 0 && (
        <div className="flex justify-end">
          <Button variant="tertiary" size="sm" onClick={() => write(rest)} aria-label={`${restLabel} of the ${kind === 'ros' ? 'asked' : 'examined'} systems`}>
            {restLabel}
          </Button>
        </div>
      )}
      {lines.map((line) => {
        const anchor = noteItemAnchor(line.code);
        const derived = systemState(line, values);
        if (readOnly) {
          const text = readText(kind, line, values);
          return (
            <Fragment key={line.code}>
              <Row id={anchor} label={line.display}>
                <span className={`m-0 text-size-sm ${text ? 'text-text-primary' : 'text-text-secondary'}`} data-system-read={line.code}>
                  {text || stateLabel(kind, 'none')}
                </span>
              </Row>
              <RiskFindingLines kind={kind} line={line} values={values} readOnly write={write} />
            </Fragment>
          );
        }
        const state: SystemState = derived === 'positive' || opened.includes(line.code) ? 'positive' : derived;
        const choose = (next: string) => {
          // '' = the chosen chip clicked again (an exam line has no "Not examined" chip): clears the line.
          const to = (next === '' ? 'none' : next) as SystemState;
          setOpened((o) => (to === 'positive' ? [...o.filter((c) => c !== line.code), line.code] : o.filter((c) => c !== line.code)));
          // Only the "Not asked" chip itself ('none'); '' is the exam's second click, which leaves the line untouched.
          setNotAsked((o) => (next === 'none' ? [...o.filter((c) => c !== line.code), line.code] : o.filter((c) => c !== line.code)));
          write(stateChange(kind, line, values, to));
        };
        const findings = pickableFindings(line);
        // The answers the picks do not show: denied / not present, and an exam's normal statements.
        const besides = readText(kind, { ...line, findings: line.findings.filter((f) => !(f.kind === 'finding' && values[f.linkId] === true)) }, values);
        return (
          <Fragment key={line.code}>
          <Row id={anchor} label={line.display} align="start">
            <ChipChoice
              options={(nurseRos ? nurseRosOptions(line) : stateOptions(kind, line)).map((o) => {
                // D-77: "Normal" carries what it records (the exam system's normal statements), inside the open list.
                const description = o.value === 'negative' ? normalDescription(kind, line) : undefined;
                return { value: o.value, label: o.label, ...(description ? { description } : {}) };
              })}
              value={state === 'none' && derived === 'none' && !notAsked.includes(line.code) ? null : state}
              onChange={choose}
              clearable={kind === 'exam' || nurseRos}
              aria-label={line.display}
            />
            {state === 'positive' && findings.length > 0 && (
              <ChipChoice
                multiple
                options={findings.map((f) => ({ value: f.linkId, label: f.text }))}
                value={positives(line, values)}
                onChange={(picked) => write(findingsChange(line, values, picked))}
                aria-label={`${line.display} findings`}
              />
            )}
            {besides && state !== 'none' && (
              <span className="text-size-sm text-text-secondary" data-system-besides={line.code}>
                {besides}
              </span>
            )}
          </Row>
          <RiskFindingLines kind={kind} line={line} values={values} readOnly={false} write={write} nurseRos={nurseRos} />
          </Fragment>
        );
      })}
    </div>
  );
}

/** A system's risk or suicide findings, each on its OWN line with an explicit answer (Codex P2 on K5): never part of
 *  the system's pick, and never written by a system-wide choice. */
function RiskFindingLines({ kind, line, values, readOnly, write, nurseRos = false }: { kind: SystemKind; line: SystemLine; values: AnswerMap; readOnly: boolean; write: (u: readonly AnswerUpdate[]) => void; nurseRos?: boolean }) {
  return (
    <>
      {riskFindings(line).map((f) => (
        <Row key={f.linkId} id={noteItemAnchor(f.linkId)} label={f.text} align="start">
          {readOnly ? (
            <span className="m-0 text-size-sm text-text-primary" data-risk-read={f.linkId}>
              {riskReadText(kind, values[f.linkId])}
            </span>
          ) : (
            <ChipChoice
              options={(nurseRos ? NURSE_ROS_RISK_OPTIONS : riskOptions(kind)).map((o) => ({ value: o.value, label: o.label }))}
              value={riskValue(values[f.linkId])}
              onChange={(next) => write([riskWrite(f.linkId, next)])}
              clearable={kind === 'exam' || nurseRos}
              aria-label={f.text}
            />
          )}
        </Row>
      ))}
    </>
  );
}

// ── Macros (the block's action) ─────────────────────────────────────────────────────────────

/**
 * The block's Macros (judge J2: the block's action, not a box's). Lists the step narrative's phrase
 * library (`offeredPhrases` for `<step>-narrative`); Apply puts the phrase at the cursor the clinician left in the
 * narrative (PROD-3149), or appends it on its own line when the narrative has no cursor, read from the live response; "Create macro from current data" opens the Settings macro
 * dialog with the narrative. `.` and `/` inside the field itself are NoteFieldLine's `commands`.
 */
function BlockMacros(
  p: StepBlockProps & {
    item: QItem;
    onFill: (linkId: string, macro: NoteMacro, anchor?: TriggerAnchor) => void;
    onWriteText: (linkId: string, text: string) => void;
    readOnly: boolean;
    /** Where the preview opens: the block body (PROD-3149), not the header Macros sits in. */
    previewContainer: HTMLDivElement | null;
  },
) {
  const { item, step, phrases, author } = p;
  // PROD-3149: Apply goes to the cursor the clinician left in the narrative, so cursors are recorded from now on.
  useEffect(() => trackFieldCarets(), []);
  // The same phrases as the narrative's `.` menu (PROD-2824 targeting; pieces only with the switch on).
  const discipline = phrases?.discipline;
  const editors = useMemo(() => lineEditors(step, author, discipline), [step, author, discipline]);
  const offered = useMemo(
    () => offeredPhrasesIn(phrases?.macros ?? [], editors, { pieces: !!phrases?.pieces }),
    [phrases?.macros, phrases?.pieces, editors],
  );
  // G8 (Part 2): the menu sorts its rows (own first, then by trigger); the complaint's phrases go before them all.
  const menuMacros = useMemo<DsNoteMacro[]>(() => toMenuMacros(offered, phrases?.suggestFor?.(editors) ?? []), [offered, phrases, editors]);
  const label = item.text ?? 'Notes';
  const current = () => {
    const v = p.liveValues()[item.linkId];
    return typeof v === 'string' ? v : '';
  };
  // Samer, 4 Nov: an inserted phrase could not be undone (the write replaces the field, so the browser's own undo is lost).
  // The last insert is kept until the field changes again; "Undo insert" writes back exactly what was there before.
  const [lastInsert, setLastInsert] = useState<{ before: string; after: string } | null>(null);
  const canUndo = !!lastInsert && current() === lastInsert.after && !p.readOnly;
  return (
    <>
    {canUndo ? (
      <Button
        type="button"
        variant="tertiary"
        size="sm"
        onClick={() => {
          p.onWriteText(item.linkId, lastInsert.before);
          setLastInsert(null);
        }}
      >
        Undo insert
      </Button>
    ) : null}
    <MacroMenu
      title={`${label} · phrases`}
      macros={menuMacros}
      computePreview={(m) => {
        const macro = offered.find((x) => x.id === m.id);
        const text = macro ? phrasePlainText(macro) : '';
        return {
          summary: macro && carriesPieces(macro) ? `Opens “${m.name}” to fill, then adds it to ${label}.` : `Adds “${m.name}” to ${label}.`,
          lines: [...text.split('\n').filter(Boolean).slice(0, 3), 'Nothing else in the note changes.'],
          applyLabel: 'Insert phrase',
        };
      }}
      onApply={(m) => {
        const macro = offered.find((x) => x.id === m.id);
        if (!macro) return;
        // PROD-3149: at the cursor the clinician left in the narrative; on its own line when it has none.
        const anchor = caretAnchorIn(noteItemAnchor(item.linkId));
        // A piece phrase is filled first (the panel under the narrative), never pasted with open pieces.
        if (carriesPieces(macro)) {
          p.onFill(item.linkId, macro, anchor);
          return;
        }
        const text = current();
        const at = anchor ? placeAt(anchor, text) : undefined;
        const next = at !== undefined ? spliceAt(text, at, phrasePlainText(macro), true) : insertAt(text, text.length, phrasePlainText(macro)).text;
        // Through the line's own write, so pieces already in the narrative are kept (PROD-2961).
        p.onWriteText(item.linkId, next);
        setLastInsert({ before: text, after: next });
      }}
      onCreateFromCurrent={() => phrases?.onCreateFromText?.(current())}
      disabled={!phrases || p.readOnly}
      previewContainer={p.previewContainer}
    />
    </>
  );
}

// ── The Sign list (UC-29c) ───────────────────────────────────────────────────────────────────

/**
 * Every required item still blank, in every section of the note, listed in the Wrap-up block where
 * Sign is. Each name goes to its section. It informs; it does not block Sign — the server check is
 * G21 (PROD-2814) and the host's sign gates are unchanged.
 */
function SignList({ questionnaire, navigableSteps, response, onStepChange, recordsFrozen }: StepBlockProps) {
  // The same blanks, and the same jump, as the floating sign bar (sign-blanks.ts).
  const open = useMemo(() => signBlanks(questionnaire, navigableSteps, response), [questionnaire, navigableSteps, response]);
  // Design desk CHANGES on e9cf22f: once the PROVIDER has signed (`recordsFrozen` is the provider sign —
  // the page's `signedFreeze.records`), "Before you sign" is gone — there is nothing left to sign — for the
  // physician and the nurse alike. It stays gone while amending: an amendment is saved with "Save amendment",
  // never signed again, so a list titled "Before you sign" would point at an action that no longer exists. A
  // nurse-only sign is not a provider sign, so the list still shows its blanks.
  if (recordsFrozen || open.length === 0) return null;
  const go = (step: PaStepKey, linkId: string, label: string) => goToSignBlank({ step, linkId, label }, onStepChange);
  return (
    <Banner variant="warning" title={`Before you sign · ${open.length} required ${open.length === 1 ? 'item is' : 'items are'} blank`} data-testid="sign-list">
      <ul className="m-0 flex list-none flex-wrap gap-x-4 gap-y-1 p-0">
        {open.map((o) => (
          // A link per blank that WRAPS (the DS Button's label never wraps, so a long item name clipped at
          // 1440 — the independent review, 6 Oct): a native button in the DS link ink, the full name shown,
          // at least 24px tall (WCAG 2.5.8, the frame v2 audit's target rule).
          <li key={o.linkId} className="min-w-0 max-w-full">
            <button
              type="button"
              onClick={() => go(o.step, o.linkId, o.label)}
              className="inline-flex min-h-6 cursor-pointer items-center border-0 bg-transparent p-0 text-left font-sans text-size-sm font-semibold text-brand-primary underline underline-offset-2 hover:text-brand-depth focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
            >
              {o.label}
            </button>
          </li>
        ))}
      </ul>
    </Banner>
  );
}

